import { Button } from "@nimbus-ds/components/Button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getLocale } from "./i18n";
import { TabIcon, type TabIconName } from "./TabIcons";
import {
  getSessionToken,
  getStoreInfo,
  getUserAdminInfo,
} from "@tiendanube/nexo";

import { Avatar } from "./avatars";
import { BillingHealthBanner } from "./BillingHealthBanner";
import { AppVersion, TiendaNubeLogo } from "./BrandHeader";
import { Chip } from "./Chip";
import { GA4PropertyPickerModal } from "./GA4PropertyPickerModal";
import { MetaAccountPickerModal } from "./MetaAccountPickerModal";
import { HeroEditorial } from "./HeroEditorial";
import { HeroMaxiWeek } from "./HeroMaxiWeek";
import { InsightCard } from "./InsightCard";
import { CommunicationPreferenceControl } from "./CommunicationPreferenceControl";
import { RecentActivityRowItem } from "./RecentActivityRowItem";
import { ResetUsageCard } from "./ResetUsageCard";
import { providerLogoFor } from "./SourceLogos";
import { DashboardSkeleton } from "./Skeleton";
import { Sparkline } from "./Sparkline";
import { SourceRack } from "./SourceRack";
import { TuEquipo } from "./TuEquipo";
import { RecommendationCard as SharedRecommendationCard } from "./RecommendationCard";
import { BRAND_VOICE_CLOSER_ES } from "./agentCopy";
import nexo from "./NexoClient";
import {
  disconnectConnector,
  exchangeNexoSessionToken,
  fetchAgentCatalog,
  fetchBootstrap,
  fetchRecommendations,
  patchRecommendation,
  type PatchRecommendationBody,
} from "./api";
import { ActionsBoard } from "./ActionsBoard";
import { ActionsFocusStrip } from "./ActionsFocusStrip";
import { REC_LIST_QUERY_KEY } from "./recCache";
import { openConnectorOAuthTab } from "./oauthConnect";
import {
  mutateBootstrapRecommendationState,
  replaceBootstrapRecommendation,
} from "./AgentInsights";
import { useSessionStore } from "./session";
import type {
  AgentInstallation,
  ApprovalSummary,
  BootstrapPayload,
  PlanTodayRow,
  RecentActivityRow,
  RecommendationState,
  SourceState,
  WorkItem,
} from "./types";

const DEFAULT_AGENT_ACCENT = "#0e8a7c";

export function Dashboard() {
  // Session bootstrap moved to <SessionGate /> at App level so deep links to
  // any route get a JWT before queries fire (revamp 2026-05-12).
  const accessToken = useSessionStore((s) => s.accessToken);
  if (!accessToken) {
    // SessionGate already shows a skeleton/error; this is a safety fallback.
    return <DashboardSkeleton />;
  }
  return <DashboardContent accessToken={accessToken} />;
}

function DashboardContent({ accessToken }: { accessToken: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const setAccessToken = useSessionStore((s) => s.setAccessToken);
  const activeTab = useDashboardTab();

  // After connecting a source (GA4 / Meta) the OAuth callback returns to
  // `/?installed=<kind>&needs_picker=1`. Two things must happen: (1) land the
  // merchant on the Fuentes tab so they see the source they just connected
  // (the callback drops them on "/" = home otherwise), and (2) surface the
  // matching account picker — the credential has no property/ad-account bound
  // yet, so reads stay dead until one is chosen (GA4: "missing property_id";
  // Meta: "missing selected_account_id").
  const [ga4PickerOpen, setGa4PickerOpen] = useState(false);
  const [metaPickerOpen, setMetaPickerOpen] = useState(false);
  // The connector whose consent tab the merchant just opened. OAuth now opens
  // in a NEW TAB (it has to escape the TN admin iframe — Google/Meta refuse to
  // be framed), so the `?installed=…&needs_picker=1` return lands in that other
  // tab, not here. We remember the kind so that when the merchant switches back
  // we refetch and prompt the matching picker in this authenticated surface.
  const pendingConnectKindRef = useRef<string | null>(null);
  const openPickerFor = useCallback((kind: string) => {
    if (kind === "google_analytics_4") setGa4PickerOpen(true);
    else if (kind === "meta_ads") setMetaPickerOpen(true);
  }, []);
  const registerPendingConnect = useCallback((kind: string) => {
    pendingConnectKindRef.current = kind;
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const installed = params.get("installed");
    if (!installed) return;
    // Step 1: route to Fuentes (preserving the one-shot params) so the picker
    // opens on the tab where the new source is visible.
    if (location.pathname !== "/fuentes") {
      navigate(`/fuentes${location.search}`, { replace: true });
      return;
    }
    // Step 2: open the picker for the connector that was just installed.
    if (params.get("needs_picker") === "1") {
      if (installed === "google_analytics_4") setGa4PickerOpen(true);
      else if (installed === "meta_ads") setMetaPickerOpen(true);
    }
  }, [location.search, location.pathname, navigate]);

  const clearPickerParams = () => {
    // Strip the one-shot query params so a refresh doesn't reopen the picker.
    const params = new URLSearchParams(location.search);
    params.delete("needs_picker");
    params.delete("installed");
    params.delete("store_id");
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ""}`, { replace: true });
  };
  const closeGa4Picker = () => {
    setGa4PickerOpen(false);
    clearPickerParams();
  };
  const closeMetaPicker = () => {
    setMetaPickerOpen(false);
    clearPickerParams();
  };
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["bootstrap"],
    queryFn: () => fetchBootstrap(accessToken),
  });

  // When the merchant returns from the new-tab consent flow, refetch so the
  // freshly-persisted credential shows up (and `needs_selection` flips on for
  // Meta/GA4, which the effect below turns into a picker prompt).
  useEffect(() => {
    function onReturn() {
      if (pendingConnectKindRef.current && document.visibilityState === "visible") {
        void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
      }
    }
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [queryClient]);

  // Auto-open the picker once the pending connector reports it needs a
  // selection. One-shot: clearing the ref prevents reopening after the merchant
  // closes the picker. The card also exposes a manual "elegí tu cuenta" CTA, so
  // this is a convenience path, not the only one.
  useEffect(() => {
    const kind = pendingConnectKindRef.current;
    if (!kind) return;
    const source = data?.sources.find((s) => s.kind === kind);
    if (source?.needs_selection) {
      openPickerFor(kind);
      pendingConnectKindRef.current = null;
    }
  }, [data, openPickerFor]);

  // Catalog total feeds the "X de N agentes activos" line in the hero. The
  // bootstrap payload only carries the hired roster, so the denominator needs
  // its own (cheap, cached) fetch to reflect what the merchant *could* hire.
  const catalogQuery = useQuery({
    queryKey: ["catalog"],
    queryFn: () => fetchAgentCatalog(),
    staleTime: 60 * 1000,
  });
  const catalogTotal = catalogQuery.data?.agents.length;

  // Compute the activity trend unconditionally so the hook order stays stable
  // across renders. `data` may be undefined during initial fetch — the memo
  // returns an empty array in that case and the hero hides the sparkline.
  const trend = useMemo(
    () => (data ? sevenDayActivityTrend(data) : []),
    [data],
  );

  // L153 Nico — closes L149-2. When the dashboard transitions from
  // `DashboardSkeleton` (loading) → live render (data ready), some screen
  // readers do NOT auto-announce the `aria-busy` toggle on the
  // skeleton's `<main>`. A small sr-only `aria-live="polite"` region
  // carries an explicit "Tablero listo" announcement on the first
  // successful data load. The text auto-clears after 1500ms so future
  // re-renders don't re-announce; the `hasAnnouncedRef` guard ensures
  // the announcement fires exactly once per session.
  const [readyAnnouncement, setReadyAnnouncement] = useState("");
  const hasAnnouncedReadyRef = useRef(false);
  useEffect(() => {
    if (hasAnnouncedReadyRef.current) return;
    if (isLoading || !data) return;
    hasAnnouncedReadyRef.current = true;
    setReadyAnnouncement("Tablero listo");
    const t = window.setTimeout(() => setReadyAnnouncement(""), 1500);
    return () => window.clearTimeout(t);
  }, [isLoading, data]);

  const reconnectTiendaNube = useMutation({
    mutationFn: async () => {
      const sessionToken = await getSessionToken(nexo);
      if (!sessionToken) {
        throw new Error("TiendaNube no devolvió una sesión válida.");
      }
      const [storeInfo, userInfo] = await Promise.all([
        getStoreInfo(nexo).catch(() => undefined),
        getUserAdminInfo(nexo).catch(() => undefined),
      ]);
      return exchangeNexoSessionToken(sessionToken, storeInfo, userInfo);
    },
    onSuccess: async (token) => {
      setAccessToken(token);
      await queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
  });

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  if (isError || !data) {
    return (
      <FullPageState
        title="No pudimos cargar el tablero"
        body={
          error instanceof Error
            ? error.message
            : "Intentá volver a abrir la app."
        }
      />
    );
  }

  const freshness = (() => {
    const latest = data.sources
      .map((source) => source.last_success_at)
      .filter(Boolean)
      .sort()
      .at(-1);
    return latest ? formatDateTime(latest) : "sin lecturas todavía";
  })();

  const hasHiredAgents = data.summary.hired_agents > 0;
  // L86: the Hoy run-now CTA must target an agent the backend will actually
  // run — a paused/archived/firing/modifying installation rejects the run
  // and surfaces a confusing error mid-LiveRunPanel. Pick the first agent in
  // `lifecycle_state === "active"`. When no agent is active but the merchant
  // has hired ones, surface a "Tu equipo está en pausa" fallback that routes
  // to the first installation's Configuración to reactivate.
  const firstActiveAgent =
    data.agents.find((a) => a.lifecycle_state === "active") ?? null;
  const firstInactiveAgent = !firstActiveAgent ? data.agents[0] ?? null : null;

  // Team pulse for the hero's Estado-del-equipo board (ribbon stats +
  // per-agent sparklines). Every cell is grounded on the bootstrap payload —
  // real (connected-or-attempted) sources only, open recs, and the backend's
  // 7-day per-agent finding projection. Shared by both home heroes.
  const realSources = data.sources.filter((s) => !s.available);
  const teamPulse = {
    openActions: data.plan_today_total ?? (data.plan_today ?? []).length,
    healthySources: realSources.filter(
      (s) => s.state === "healthy" || s.state === "connected",
    ).length,
    totalSources: realSources.length,
    weekByAgent: data.summary.week_findings_by_agent ?? {},
  };

  // Embedded `agent: {...}` blocks on approvals/recent-activity only carry
  // id/slug/name; the roster is the only place `avatar_slug`/`accent` live.
  const agentsById = new Map<string, AgentInstallation>(
    data.agents.map((agent) => [agent.id, agent]),
  );
  const openAgentChat = (installationId: string) => {
    navigate(`/chats?agent=${installationId}`);
  };

  if (data.store.install_state === "uninstalled") {
    const uninstallAt =
      data.store.tiendanube_signal?.last_uninstall_at ?? null;
    const uninstallLine = uninstallAt
      ? `Detectamos la desconexión el ${formatUninstallTimestamp(uninstallAt)}. `
      : "";
    return (
      <FullPageState
        title="La app fue desinstalada"
        body={`${uninstallLine}Volvé a conectar la sesión de TiendaNube para recuperar tu tablero. El historial de tus agentes queda guardado; solo renovamos permisos.`}
        actionLabel={
          reconnectTiendaNube.isPending
            ? "Conectando..."
            : "Volver a conectar TiendaNube"
        }
        onAction={() => reconnectTiendaNube.mutate()}
        actionDisabled={reconnectTiendaNube.isPending}
        error={
          reconnectTiendaNube.isError
            ? reconnectTiendaNube.error instanceof Error
              ? reconnectTiendaNube.error.message
              : "No pudimos reconectar TiendaNube."
            : ""
        }
      />
    );
  }

  // L146 Lucía — partial-state lane (DESIGN.md §5 + §11.1). When the
  // bootstrap reports any unhealthy source, surface a warning banner ABOVE
  // the tab nav so the merchant sees the partial-data signal on the Hoy
  // landing — the Fuentes tab badge alone is not enough for a merchant who
  // skips the connector tab.
  const unhealthySources = data.summary.unhealthy_sources;

  return (
    <main className="dashboard">
      {/* L153 Nico — sr-only ready announcement (DESIGN.md §5 loading→live
          transition; closes L149-2). Renders empty most of the time;
          briefly carries "Tablero listo" on the first successful data
          load so SRs that don't auto-announce the `aria-busy` toggle
          still get a completion signal. */}
      <div
        className="sr-only"
        aria-live="polite"
        data-testid="dashboard-ready-announcement"
      >
        {readyAnnouncement}
      </div>
      {/* Centralized-billing status banner (2026-06-16). Fails open: shows
          only for past_due / suspended; an unknown status (incl. a billing
          outage) renders nothing so it never blocks the dashboard. Mounted
          above the hero so it's seen on any tab. */}
      <BillingHealthBanner />
      {/* L157 Lucía — Dashboard `/` hero redesign. The home opens on
          Maxi's weekly read (DESIGN.md §2.5 vision for `/`) ONLY when
          the merchant has actually hired Maxi. Otherwise — including
          the case where other agents are hired but the manager slug
          isn't — the hero falls back to the brand-led `<HeroEditorial>`
          greeting that respects the merchant's roster choice. (DA-LIVE-1
          decision: a Maxi-hire CTA in the home hero on a no-Maxi store
          is a coercive upsell, not a product hero. See DESIGN.md §2.5
          L157 Sebastián ratification.) */}
      {hasHiredAgents &&
      data.manager_brief &&
      (data.manager_brief.available ||
        data.manager_brief.reason === "no_run_yet") ? (
        <HeroMaxiWeek
          brief={data.manager_brief}
          storeName={data.store.name}
          freshness={`datos ${freshness}`}
          agents={data.agents}
          catalogTotal={catalogTotal}
          today={data.today}
          pulse={teamPulse}
        />
      ) : (
        <HeroEditorial
          storeName={data.store.name}
          freshness={`datos ${freshness}`}
          hasHiredAgents={hasHiredAgents}
          onPrimaryAction={() => navigate("/equipo")}
          onSecondaryAction={() => navigate("/chats")}
          agents={data.agents}
          catalogTotal={catalogTotal}
          today={data.today}
          pulse={teamPulse}
        />
      )}

      {unhealthySources > 0 ? (
        <DashboardPartialBanner
          unhealthyCount={unhealthySources}
          totalSources={data.sources.filter((s) => !s.available).length}
          onReview={() => navigate("/fuentes")}
        />
      ) : null}

      <GA4PropertyPickerModal
        open={ga4PickerOpen}
        onClose={closeGa4Picker}
        onSelected={() =>
          queryClient.invalidateQueries({ queryKey: ["bootstrap"] })
        }
      />

      <MetaAccountPickerModal
        open={metaPickerOpen}
        onClose={closeMetaPicker}
        onSelected={() =>
          queryClient.invalidateQueries({ queryKey: ["bootstrap"] })
        }
      />

      <div className="dashboardTabsRow">
        <DashboardTabsNav active={activeTab} payload={data} />
        <CommunicationPreferenceControl align="end" />
      </div>

      {activeTab === "stats" && (
        <StatsPanel data={data} trend={trend} />
      )}
      {activeTab === "acciones" && (
        <AccionesPanel data={data} openAgentChat={openAgentChat} />
      )}
      {activeTab === "analisis" && (
        <AnalisisPanel
          data={data}
          agentsById={agentsById}
          firstActiveAgent={firstActiveAgent}
          firstInactiveAgent={firstInactiveAgent}
          hasHiredAgents={hasHiredAgents}
          openAgentChat={openAgentChat}
        />
      )}
      {activeTab === "fuentes" && (
        <FuentesPanel
          data={data}
          onConnectStarted={registerPendingConnect}
          onOpenPicker={openPickerFor}
        />
      )}
      {activeTab === "equipo" && (
        <EquipoPanel
          hiredAgents={data.agents}
          recentActivity={data.recent_activity ?? []}
        />
      )}

      {/* L146 Lucía — closer only renders on the landing tab. The other
          four tabs are scan surfaces where the editorial closer reads as
          decorative-divider-overuse (DESIGN.md §10). */}
      {activeTab === "stats" ? <BrandVoiceCloser /> : null}
    </main>
  );
}

// L146 Lucía — partial-state banner for the Dashboard shell. Warning tone,
// names how many fuentes are stale, closes with a verb pointing at /fuentes.
// Renders above the sticky tab nav so it occupies the top of the merchant's
// first scroll and then naturally scrolls away — non-blocking, not modal.
function DashboardPartialBanner({
  unhealthyCount,
  totalSources,
  onReview,
}: {
  unhealthyCount: number;
  totalSources: number;
  onReview: () => void;
}) {
  const fuenteWord = unhealthyCount === 1 ? "fuente" : "fuentes";
  const verb = unhealthyCount === 1 ? "está" : "están";
  const headline =
    totalSources > 0
      ? `${unhealthyCount} de ${totalSources} ${fuenteWord} ${verb} con problemas.`
      : `${unhealthyCount} ${fuenteWord} ${verb} con problemas.`;
  return (
    <aside
      className="dashboardPartialBanner"
      role="status"
      aria-live="polite"
      data-testid="dashboard-partial-banner"
    >
      <span className="dashboardPartialBanner__dot" aria-hidden="true" />
      <div className="dashboardPartialBanner__copy">
        <p className="dashboardPartialBanner__headline">{headline}</p>
        <p className="dashboardPartialBanner__body">
          Tu equipo sigue leyendo lo que puede, pero los hallazgos pueden
          quedarse cortos hasta que reconectes.
        </p>
      </div>
      <Button
        appearance="neutral"
        className="dashboardPartialBanner__cta"
        onClick={onReview}
      >
        Revisar fuentes
      </Button>
    </aside>
  );
}

// L147 Bruno — tab-level masthead. Every Dashboard tab opens with the same
// primitive so first-eye landing reads consistently across all five
// (DESIGN.md §11.1 parity contract).
//
// Editorial masthead (2026-06-20, ref-matched): a small primary-blue
// uppercase kicker (the `eyebrow` prop) over a large 22px bold near-black
// headline (the `helper` prop, where each tab's engaging one-liner lives).
// The headline is the focal point; the kicker labels it. It replaced the
// original flat two-line all-gray treatment. Styling lives in the
// `.dashboardTabEyebrow*` rules in styles.css.
//
// L148 Clara — closes the L147-DA-1 a11y regression. The eyebrow renders as
// an `<h2>` (not a `<span>`) so screen-reader heading navigation lands on
// the tab's name; the visual treatment stays bound to
// `.dashboardTabEyebrow__eyebrow` so the merchant sees zero pixel delta.
// The per-card titles inside each tab remain `<h3>` — clean two-level
// semantic hierarchy: tab `<h2>` → card `<h3>`. The
// `<StatsSubSection>` eyebrow stays a decorative `<span>` (it's a label,
// not a section heading) so the heading tree doesn't double-nest.
function DashboardTabEyebrow({
  eyebrow,
  helper,
  testId,
}: {
  eyebrow: string;
  helper?: string;
  testId?: string;
}) {
  return (
    <div className="dashboardTabEyebrow" data-testid={testId}>
      <h2 className="dashboardTabEyebrow__eyebrow">{eyebrow}</h2>
      {helper ? (
        <p className="dashboardTabEyebrow__helper">{helper}</p>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab routing — `/`, `/stats`, `/acciones`, `/analisis`, `/fuentes`, `/equipo`
// all render the Dashboard. Active tab is derived from the URL path so each
// surface is deep-linkable (the TN admin echoes the route via NexoSyncRoute,
// so merchants can share direct links to a specific dashboard surface).
// ---------------------------------------------------------------------------
export type DashboardTab =
  | "stats"
  | "acciones"
  | "analisis"
  | "fuentes"
  | "equipo";

const DASHBOARD_TAB_BY_PATH: Record<string, DashboardTab> = {
  "/": "stats",
  "/stats": "stats",
  "/acciones": "acciones",
  "/analisis": "analisis",
  "/fuentes": "fuentes",
  "/equipo": "equipo",
};

function useDashboardTab(): DashboardTab {
  const { pathname } = useLocation();
  return DASHBOARD_TAB_BY_PATH[pathname] ?? "stats";
}

// L149 Nico — Spanish SR description for the per-tab numeric badge. Before
// L149 the badge carried `aria-hidden="true"`, so screen-reader users
// navigating the tabs heard "Stats / Acciones / Análisis / Fuentes /
// Equipo" with NO mention of the count — and the badge tone
// (`warning`/`danger`/`neutral`/`accent`/`primary`) carries meaning that
// was color-only for assistive tech. The helper returns a Spanish phrase
// the SR reads alongside the visual digit; the visual treatment stays
// unchanged. Plural-aware per tab + tone-aware where the tone changes the
// substantive verb ("alertas" for warning/danger on Fuentes, "pendientes"
// for action queue on Acciones, etc.). Closes L148-2.
export function tabBadgeAriaLabelEs(
  tab: DashboardTab,
  value: number,
  tone: "primary" | "warning" | "danger" | "neutral" | "accent",
): string {
  if (tab === "acciones") {
    return value === 1 ? "1 pendiente" : `${value} pendientes`;
  }
  if (tab === "analisis") {
    return value === 1 ? "1 hallazgo nuevo" : `${value} hallazgos nuevos`;
  }
  if (tab === "fuentes") {
    if (tone === "danger" || tone === "warning") {
      return value === 1 ? "1 fuente con alerta" : `${value} fuentes con alertas`;
    }
    return value === 1 ? "1 fuente conectada" : `${value} fuentes conectadas`;
  }
  if (tab === "equipo") {
    return value === 1 ? "1 agente contratado" : `${value} agentes contratados`;
  }
  return value === 1 ? "1 ítem" : `${value} ítems`;
}

function DashboardTabsNav({
  active,
  payload,
}: {
  active: DashboardTab;
  payload: BootstrapPayload;
}) {
  const today = payload.today.length;
  const approvals = payload.approvals.length;
  const openRecs = payload.plan_today_total ?? (payload.plan_today ?? []).length;
  const pending = approvals + openRecs;
  const fuentesAlerts = payload.summary.unhealthy_sources;
  const sources = payload.sources.length;
  const hiredAgents = payload.summary.hired_agents;

  const tabs: Array<{
    key: DashboardTab;
    to: string;
    label: string;
    icon: TabIconName;
    badge?: { value: number; tone: "primary" | "warning" | "danger" | "neutral" | "accent" };
  }> = [
    // Order (2026-06-22): Equipo → Análisis → Acciones → Fuentes → Stats.
    // Equipo (the roster — the product's core, the agents you hire) leads,
    // then the per-agent output tabs, with Stats ("Tus números") last as a
    // summary/overview tab.
    {
      key: "equipo",
      to: "/equipo",
      label: "Equipo",
      icon: "equipo",
      badge:
        hiredAgents > 0
          ? { value: hiredAgents, tone: "accent" }
          : undefined,
    },
    {
      key: "analisis",
      to: "/analisis",
      label: "Análisis",
      icon: "analisis",
      badge: today > 0 ? { value: today, tone: "primary" } : undefined,
    },
    {
      key: "acciones",
      to: "/acciones",
      label: "Acciones",
      icon: "acciones",
      badge: pending > 0 ? { value: pending, tone: "warning" } : undefined,
    },
    {
      key: "fuentes",
      to: "/fuentes",
      label: "Fuentes",
      icon: "fuentes",
      badge:
        fuentesAlerts > 0
          ? { value: fuentesAlerts, tone: "danger" }
          : sources > 0
            ? { value: sources, tone: "neutral" }
            : undefined,
    },
    { key: "stats", to: "/stats", label: "Stats", icon: "stats" },
  ];

  return (
    <nav className="dashboardTabs" aria-label="Secciones del tablero" data-testid="dashboard-tabs">
      {tabs.map((tab) => (
        <NavLink
          key={tab.key}
          to={tab.to}
          end={tab.to === "/stats"}
          className={({ isActive }) => {
            const isActiveForTab = tab.key === active || isActive;
            return isActiveForTab
              ? "dashboardTabs__tab dashboardTabs__tab--active"
              : "dashboardTabs__tab";
          }}
          aria-current={tab.key === active ? "page" : undefined}
          data-testid={`dashboard-tab-${tab.key}`}
        >
          <TabIcon name={tab.icon} />
          <span className="dashboardTabs__label">{tab.label}</span>
          {tab.badge ? (
            // L149 Nico — closes L148-2. The visual digit stays as the
            // badge's text content; an `sr-only` sibling carries the
            // Spanish phrase the screen-reader actually reads alongside
            // the tab name. The badge no longer carries `aria-hidden`
            // wholesale; only the digit is implicitly de-emphasized for
            // SR (it would read as a bare number otherwise — the
            // `sr-only` text spells out what the number MEANS).
            <span
              className={`dashboardTabs__badge dashboardTabs__badge--${tab.badge.tone}`}
              data-testid={`dashboard-tab-badge-${tab.key}`}
            >
              <span aria-hidden="true">{tab.badge.value}</span>
              <span className="sr-only">
                {" — "}
                {tabBadgeAriaLabelEs(tab.key, tab.badge.value, tab.badge.tone)}
              </span>
            </span>
          ) : null}
        </NavLink>
      ))}
      {/* Facturación — embedded billing surface (separate app). Shown only when
          the BACKEND reports billing configured (`billing_enabled`). It is a real
          destination (its own route), so it navigates away from the tablero
          rather than switching a panel. */}
      {payload.billing_enabled ? (
        <NavLink
          to="/facturacion"
          className={({ isActive }) =>
            isActive
              ? "dashboardTabs__tab dashboardTabs__tab--active"
              : "dashboardTabs__tab"
          }
          data-testid="dashboard-tab-facturacion"
        >
          <TabIcon name="facturacion" />
          <span className="dashboardTabs__label">Facturación</span>
        </NavLink>
      ) : null}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Stats panel — "Tus números".
//
// Layout: KPI row (8 large counters) → charts row (severity donut +
// 7-day activity bars) → breakdown row (per-agent observation totals).
// Every KPI tile that maps to a tab acts as a deep link so the merchant
// can pivot from "I see a number I care about" to "show me the rows behind
// it" in one click.
// ---------------------------------------------------------------------------
function StatsPanel({
  data,
  trend,
}: {
  data: BootstrapPayload;
  trend: number[];
}) {
  const navigate = useNavigate();
  const today = data.today;
  const activeAgents = data.agents.filter(
    (a) => a.lifecycle_state === "active",
  ).length;
  const pausedAgents = data.agents.filter(
    (a) => a.lifecycle_state !== "active",
  ).length;
  const criticalToday = today.filter((t) => t.severity === "critical").length;
  const warnToday = today.filter((t) => t.severity === "warn").length;
  const infoToday = today.filter((t) => t.severity === "info").length;
  const citationsToday = today.reduce(
    (sum, item) => sum + (item.citations?.length ?? 0),
    0,
  );
  const sevenDayTotal = trend.reduce((a, b) => a + b, 0);
  // Health KPIs count only REAL (connected-or-attempted) sources. Synthetic
  // "available" rows (connectable but never linked, e.g. GA on a TN-only
  // store) are opportunities, not connections — counting them would render a
  // false "1/3 fuentes saludables" warning on a perfectly healthy store.
  const realSources = data.sources.filter((s) => !s.available);
  const healthySources = realSources.filter(
    (s) => s.state === "healthy" || s.state === "connected",
  ).length;
  const totalSources = realSources.length;
  const openRecs = data.plan_today_total ?? (data.plan_today ?? []).length;
  const actionsExecuted7d = (data.recent_activity ?? []).filter((row) =>
    row.kind.startsWith("action_executed"),
  ).length;
  const actionsReverted7d = (data.recent_activity ?? []).filter(
    (row) => row.kind === "action_reverted",
  ).length;

  // Per-agent observations today — drives the breakdown bars below the
  // donut. Built from `today[]` so the count reflects what's actually
  // visible in the Análisis feed; deduped to the roster so an installation
  // that hasn't published lands as a zero-row instead of vanishing.
  const observationsByAgent = new Map<string, number>();
  for (const item of today) {
    const id = item.agent_installation_id;
    if (!id) continue;
    observationsByAgent.set(id, (observationsByAgent.get(id) ?? 0) + 1);
  }
  const perAgentToday = data.agents.map((agent) => ({
    agent,
    count: observationsByAgent.get(agent.id) ?? 0,
  }));
  const maxPerAgent = perAgentToday.reduce((max, r) => Math.max(max, r.count), 0);

  // Per-agent severity breakdown for today's observations. v1 agents are
  // observer-only (no actor class shipped) — surfacing "Acciones por agente"
  // would always read as zero, so this chart trades for the analyst-facing
  // dimension that's actually populated: how the day's findings split by
  // severity across the team.
  //
  // We prefer the backend's `summary.today_by_agent_severity` projection
  // (DB-side aggregation over the FULL day) so the chart doesn't undercount
  // when a slug publishes more than the 20 rows the `today` slice carries.
  // When the field is absent (older bootstrap payloads, test fixtures), we
  // fall back to deriving from `data.today` — still useful, just capped.
  const serverSeverityByAgent = data.summary.today_by_agent_severity;
  const severityByAgent = new Map<
    string,
    { critical: number; warn: number; info: number }
  >();
  if (serverSeverityByAgent) {
    for (const [id, counts] of Object.entries(serverSeverityByAgent)) {
      severityByAgent.set(id, {
        critical: counts.critical ?? 0,
        warn: counts.warn ?? 0,
        info: counts.info ?? 0,
      });
    }
  } else {
    for (const item of today) {
      const id = item.agent_installation_id;
      if (!id) continue;
      const cell = severityByAgent.get(id) ?? { critical: 0, warn: 0, info: 0 };
      if (item.severity === "critical") cell.critical += 1;
      else if (item.severity === "warn") cell.warn += 1;
      else cell.info += 1;
      severityByAgent.set(id, cell);
    }
  }
  const perAgentSeverity = data.agents.map((agent) => ({
    agent,
    counts: severityByAgent.get(agent.id) ?? { critical: 0, warn: 0, info: 0 },
  }));
  const maxSeverityTotal = perAgentSeverity.reduce(
    (max, r) => Math.max(max, r.counts.critical + r.counts.warn + r.counts.info),
    0,
  );

  const kpis: Array<{
    label: string;
    value: number | string;
    unit: string;
    tone: "accent" | "primary" | "warning" | "neutral" | "danger";
    hint?: string;
    onClick?: () => void;
    sparkline?: number[];
  }> = [
    {
      label: "Equipo activo",
      value: activeAgents,
      unit: activeAgents === 1 ? "agente en turno" : "agentes en turno",
      tone: "accent",
      hint: pausedAgents > 0 ? `${pausedAgents} en pausa` : "Sin pausas",
      onClick: () => navigate("/equipo"),
    },
    {
      label: "Hallazgos hoy",
      value: today.length,
      unit: today.length === 1 ? "publicado" : "publicados",
      tone: today.length > 0 ? "primary" : "neutral",
      hint:
        criticalToday > 0
          ? `${criticalToday} crítico${criticalToday === 1 ? "" : "s"}`
          : warnToday > 0
            ? `${warnToday} advertencia${warnToday === 1 ? "" : "s"}`
            : "Sin alertas",
      onClick: () => navigate("/analisis"),
    },
    {
      label: "Actividad 7 días",
      value: sevenDayTotal,
      unit: sevenDayTotal === 1 ? "evento" : "eventos",
      tone: "accent",
      sparkline: trend,
    },
    {
      label: "Citas a tus datos",
      value: citationsToday,
      unit: citationsToday === 1 ? "cita hoy" : "citas hoy",
      tone: "primary",
      hint: "Evidencia que respalda los hallazgos",
    },
    // "Para aprobar" KPI removed (2026-06-21): v1 agents are observer/analyst
    // only, so it was a permanent "0 pendientes" tile that read as broken.
    {
      label: "Plan abierto",
      value: openRecs,
      unit: openRecs === 1 ? "recomendación" : "recomendaciones",
      tone: openRecs > 0 ? "primary" : "neutral",
      onClick: () => navigate("/acciones"),
    },
    {
      label: "Fuentes saludables",
      value: `${healthySources}/${totalSources || 0}`,
      unit: totalSources === 1 ? "fuente conectada" : "fuentes conectadas",
      tone:
        totalSources === 0
          ? "neutral"
          : healthySources === totalSources
            ? "accent"
            : "warning",
      onClick: () => navigate("/fuentes"),
    },
    {
      label: "Acciones ejecutadas",
      value: actionsExecuted7d,
      unit: "en los últimos 7 días",
      tone: actionsExecuted7d > 0 ? "accent" : "neutral",
      hint:
        actionsReverted7d > 0
          ? `${actionsReverted7d} revertida${actionsReverted7d === 1 ? "" : "s"}`
          : undefined,
    },
  ];

  return (
    <section
      className="statsPanel"
      aria-label="Estadísticas del tablero"
      data-testid="dashboard-stats-panel"
    >
      {/* L147 Bruno — was an h2 + subtitle in `.sectionEditorial__header`;
          demoted to the tab-level eyebrow so the three rungs below it
          (`statsSubSection__eyebrow` ×2 + per-card h3s) read as the only
          hierarchy on the tab. flat-type-hierarchy fix per DESIGN.md §10. */}
      <DashboardTabEyebrow
        eyebrow="Tus números"
        helper="Cómo viene tu tienda hoy, con todo lo que ya revisó tu equipo."
        testId="stats-tab-eyebrow"
      />

      <div className="statsPanel__grid">
        {kpis.map((stat) => (
          <StatBigTile key={stat.label} {...stat} />
        ))}
      </div>

      {/* L146 Lucía — sub-section eyebrow above the chart cluster. Without
          it the panel reads as one undifferentiated wall of cards
          (cramped-hierarchy). The eyebrow gives the eye a place to land
          before switching from KPI-counting to comparison-reading. */}
      <StatsSubSection
        eyebrow="Cómo se está leyendo el día"
        helper="Severidad de los hallazgos y ritmo de actividad de tu equipo."
      >
        <div className="statsPanel__charts">
          <article className="statsChartCard">
            <header className="statsChartCard__head">
              {/* L154 Bruno — h3 → h4. The Stats tab now has three-level
                  hierarchy: tab `<h2>` → sub-section `<h3>` → per-card `<h4>`. */}
              <h4 className="statsChartCard__title">Severidad de hoy</h4>
              <p className="statsChartCard__hint">
                Cómo se reparten los hallazgos publicados en las últimas horas.
              </p>
            </header>
            <SeverityDonut
              critical={criticalToday}
              warn={warnToday}
              info={infoToday}
            />
          </article>
          <article className="statsChartCard">
            <header className="statsChartCard__head">
              <h4 className="statsChartCard__title">Actividad por día</h4>
              <p className="statsChartCard__hint">
                Eventos del equipo en los últimos 7 días.
              </p>
            </header>
            <ActivityBars trend={trend} />
          </article>
        </div>
      </StatsSubSection>

      <StatsSubSection
        eyebrow="Hallazgos por agente"
        helper="Quién publicó qué hoy y cómo se reparten las severidades."
      >
        <div className="statsPanel__breakdown">
          <article className="statsBreakdownCard">
            <header className="statsBreakdownCard__head">
              <h4 className="statsBreakdownCard__title">Hallazgos hoy por agente</h4>
              <p className="statsBreakdownCard__hint">
                Quién publicó qué — abrí el detalle desde la barra.
              </p>
            </header>
            <AgentBarList rows={perAgentToday} max={maxPerAgent} suffix="publicados" />
          </article>
          <article className="statsBreakdownCard">
            <header className="statsBreakdownCard__head">
              <h4 className="statsBreakdownCard__title">
                Hallazgos por severidad
              </h4>
              <p className="statsBreakdownCard__hint">
                Cómo se reparten los hallazgos de hoy entre tu equipo.
              </p>
            </header>
            <AgentSeverityBars
              rows={perAgentSeverity}
              max={maxSeverityTotal}
            />
          </article>
        </div>
      </StatsSubSection>
      <StatsSubSection
        eyebrow="Zona de prueba"
        helper="Reiniciá tus datos para volver a probar desde cero (temporal)."
      >
        <div className="statsPanel__breakdown">
          <ResetUsageCard />
        </div>
      </StatsSubSection>
    </section>
  );
}

// L146 Lucía — lightweight sub-section wrapper used inside StatsPanel to
// break the wall-of-cards rhythm. The eyebrow is uppercase + spaced (the
// same eyebrow type pattern as `.statBigTile__label`) so the merchant
// reads it as a label, not as another title competing with the panel h2.
// The wrapper itself adds vertical space ABOVE itself so the rhythm
// matches DESIGN.md §7 ("Above a section title: 2× the within-section gap").
function StatsSubSection({
  eyebrow,
  helper,
  children,
}: {
  eyebrow: string;
  helper?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="statsSubSection">
      <div className="statsSubSection__head">
        {/* L154 Bruno — closes L148-DA-2. Was a decorative `<span>` per
            L147's two-level contract; promoted to `<h3>` so SR users
            navigating by heading land on the sub-section grouping
            ("Cómo se está leyendo el día" / "Hallazgos por agente")
            between the panel `<h2>` and the per-card `<h4>`s. Visual
            treatment unchanged (CSS class preserved + `margin: 0`
            reset on the h3 default block-margins). Three-level
            semantic depth ONLY on Stats — the other four tabs have no
            sub-section pattern and stay at the simpler two-level. */}
        <h3 className="statsSubSection__eyebrow">{eyebrow}</h3>
        {helper ? (
          <p className="statsSubSection__helper">{helper}</p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline SVG severity donut. We avoid pulling a charting library — the chart
// is two concentric circles plus one arc per non-zero segment, drawn via
// `strokeDasharray` so the visual matches across browsers. Total renders in
// the center to anchor the eye on the count, not the slice geometry.
// ---------------------------------------------------------------------------
function SeverityDonut({
  critical,
  warn,
  info,
}: {
  critical: number;
  warn: number;
  info: number;
}) {
  const total = critical + warn + info;
  const radius = 36;
  const stroke = 14;
  const cx = 50;
  const cy = 50;
  const circumference = 2 * Math.PI * radius;

  if (total === 0) {
    return (
      <div className="severityDonut severityDonut--empty">
        <svg viewBox="0 0 100 100" width={140} height={140} aria-hidden="true">
          <circle
            cx={cx}
            cy={cy}
            r={radius}
            fill="none"
            stroke="var(--color-border-soft)"
            strokeWidth={stroke}
          />
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            className="severityDonut__total"
          >
            0
          </text>
        </svg>
        <p className="severityDonut__emptyCopy">
          Sin hallazgos hoy todavía. Cuando tus agentes terminen su próxima
          corrida, vas a verlo acá.
        </p>
      </div>
    );
  }

  const segments = [
    { value: critical, color: "var(--color-danger)", label: "Críticos" },
    { value: warn, color: "var(--color-warning)", label: "Advertencias" },
    { value: info, color: "var(--color-accent)", label: "Informativos" },
  ];

  let offset = 0;
  return (
    <div className="severityDonut">
      <svg
        viewBox="0 0 100 100"
        width={140}
        height={140}
        role="img"
        aria-label={`${total} hallazgos hoy — ${critical} críticos, ${warn} advertencias, ${info} informativos`}
      >
        <circle
          cx={cx}
          cy={cy}
          r={radius}
          fill="none"
          stroke="var(--color-border-soft)"
          strokeWidth={stroke}
        />
        {segments.map((seg, i) => {
          if (seg.value === 0) return null;
          const len = (seg.value / total) * circumference;
          const dasharray = `${len} ${circumference - len}`;
          const dashoffset = -offset;
          offset += len;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={radius}
              fill="none"
              stroke={seg.color}
              strokeWidth={stroke}
              strokeDasharray={dasharray}
              strokeDashoffset={dashoffset}
              transform="rotate(-90 50 50)"
              strokeLinecap="butt"
            />
          );
        })}
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          dominantBaseline="central"
          className="severityDonut__total"
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 14}
          textAnchor="middle"
          dominantBaseline="central"
          className="severityDonut__totalLabel"
        >
          hoy
        </text>
      </svg>
      <ul className="severityDonut__legend" aria-label="Detalle de severidad">
        {segments.map((seg) => (
          <li key={seg.label} className="severityDonut__legendItem">
            <span
              className="severityDonut__swatch"
              style={{ background: seg.color }}
              aria-hidden="true"
            />
            <span className="severityDonut__legendLabel">{seg.label}</span>
            <span className="severityDonut__legendValue">{seg.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActivityBars({ trend }: { trend: number[] }) {
  if (trend.length === 0) {
    return <p className="statsChartCard__empty">Sin actividad registrada.</p>;
  }
  const max = trend.reduce((m, v) => Math.max(m, v), 0);
  const labels = ["6d", "5d", "4d", "3d", "2d", "Ayer", "Hoy"];
  return (
    <div className="activityBars" role="img" aria-label="Actividad por día">
      {trend.map((value, i) => {
        const heightPct = max > 0 ? Math.max(4, (value / max) * 100) : 4;
        return (
          <div key={i} className="activityBars__col">
            <span className="activityBars__value">{value}</span>
            <span
              className={`activityBars__bar${value === 0 ? " activityBars__bar--empty" : ""}`}
              style={{ height: `${heightPct}%` }}
            />
            <span className="activityBars__label">
              {labels[labels.length - trend.length + i] ?? ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function AgentBarList({
  rows,
  max,
  suffix,
}: {
  rows: Array<{ agent: AgentInstallation; count: number }>;
  max: number;
  suffix: string;
}) {
  const navigate = useNavigate();
  if (rows.length === 0) {
    return (
      <p className="statsBreakdownCard__empty">
        Todavía no tenés agentes contratados.
      </p>
    );
  }
  if (max === 0) {
    return (
      <p className="statsBreakdownCard__empty">
        Sin {suffix} todavía — cuando tus agentes terminen su próxima corrida vas
        a verlo acá.
      </p>
    );
  }
  // Sort by count desc so the biggest contributor is at the top.
  const sorted = [...rows].sort((a, b) => b.count - a.count);
  return (
    <ul className="agentBarList">
      {sorted.map((row) => {
        const accent = row.agent.accent_color_hex || DEFAULT_AGENT_ACCENT;
        const name = row.agent.display_name || row.agent.name;
        const widthPct = Math.max(4, (row.count / max) * 100);
        return (
          <li key={row.agent.id} className="agentBarList__row">
            <button
              type="button"
              className="agentBarList__name"
              onClick={() => navigate(`/agents/${row.agent.id}`)}
              aria-label={`${name} — ${row.count} ${suffix}`}
            >
              <Avatar
                slug={row.agent.avatar_slug}
                accent={accent}
                fallbackInitial={name.charAt(0) || "?"}
                fallbackAccent={accent}
                size={20}
                ariaLabel={name}
              />
              <span>{name}</span>
            </button>
            <div className="agentBarList__barWrap">
              <span
                className="agentBarList__bar"
                style={{ width: `${widthPct}%`, background: accent }}
              />
            </div>
            <span className="agentBarList__count">{row.count}</span>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Per-agent stacked-severity bar — one row per agent, segments sized
 * proportionally to the team's heaviest day so the chart reads as a
 * comparison rather than per-row normalization. Segments are clickable in
 * spirit (the whole row → agent detail) but tone is conveyed via fill,
 * matching the Hoy severity palette.
 */
function AgentSeverityBars({
  rows,
  max,
}: {
  rows: Array<{
    agent: AgentInstallation;
    counts: { critical: number; warn: number; info: number };
  }>;
  max: number;
}) {
  const navigate = useNavigate();
  if (rows.length === 0) {
    return (
      <p className="statsBreakdownCard__empty">
        Todavía no tenés agentes contratados.
      </p>
    );
  }
  if (max === 0) {
    return (
      <p className="statsBreakdownCard__empty">
        Sin hallazgos hoy todavía — cuando tus agentes terminen su próxima
        corrida vas a ver acá el desglose por severidad.
      </p>
    );
  }
  const sorted = [...rows].sort((a, b) => {
    const ta = a.counts.critical + a.counts.warn + a.counts.info;
    const tb = b.counts.critical + b.counts.warn + b.counts.info;
    return tb - ta;
  });
  return (
    <ul className="agentSeverityBars">
      {sorted.map((row) => {
        const accent = row.agent.accent_color_hex || DEFAULT_AGENT_ACCENT;
        const name = row.agent.display_name || row.agent.name;
        const total = row.counts.critical + row.counts.warn + row.counts.info;
        const cw = max > 0 ? (row.counts.critical / max) * 100 : 0;
        const ww = max > 0 ? (row.counts.warn / max) * 100 : 0;
        const iw = max > 0 ? (row.counts.info / max) * 100 : 0;
        return (
          <li key={row.agent.id} className="agentSeverityBars__row">
            <button
              type="button"
              className="agentBarList__name"
              onClick={() => navigate(`/agents/${row.agent.id}`)}
              aria-label={`${name} — ${row.counts.critical} crítico, ${row.counts.warn} advertencia, ${row.counts.info} informativo`}
            >
              <Avatar
                slug={row.agent.avatar_slug}
                accent={accent}
                fallbackInitial={name.charAt(0) || "?"}
                fallbackAccent={accent}
                size={20}
                ariaLabel={name}
              />
              <span>{name}</span>
            </button>
            <div className="agentSeverityBars__barWrap" title={`${total} en total`}>
              {row.counts.critical > 0 ? (
                <span
                  className="agentSeverityBars__seg agentSeverityBars__seg--critical"
                  style={{ width: `${cw}%` }}
                  title={`${row.counts.critical} crítico${row.counts.critical === 1 ? "" : "s"}`}
                />
              ) : null}
              {row.counts.warn > 0 ? (
                <span
                  className="agentSeverityBars__seg agentSeverityBars__seg--warn"
                  style={{ width: `${ww}%` }}
                  title={`${row.counts.warn} advertencia${row.counts.warn === 1 ? "" : "s"}`}
                />
              ) : null}
              {row.counts.info > 0 ? (
                <span
                  className="agentSeverityBars__seg agentSeverityBars__seg--info"
                  style={{ width: `${iw}%` }}
                  title={`${row.counts.info} informativo${row.counts.info === 1 ? "" : "s"}`}
                />
              ) : null}
            </div>
            <span className="agentBarList__count">{total}</span>
          </li>
        );
      })}
    </ul>
  );
}

function StatBigTile({
  label,
  value,
  unit,
  tone,
  hint,
  onClick,
  sparkline,
}: {
  label: string;
  value: number | string;
  unit: string;
  tone: "accent" | "primary" | "warning" | "neutral" | "danger";
  hint?: string;
  onClick?: () => void;
  sparkline?: number[];
}) {
  const className = `statBigTile statBigTile--${tone}${onClick ? " statBigTile--clickable" : ""}`;
  const content = (
    <>
      <span className="statBigTile__label">{label}</span>
      <span className="statBigTile__value">{value}</span>
      <span className="statBigTile__unit">{unit}</span>
      {hint ? <span className="statBigTile__hint">{hint}</span> : null}
      {sparkline && sparkline.length > 0 ? (
        <Sparkline
          values={sparkline}
          width={140}
          height={28}
          color="var(--color-accent)"
          ariaLabel="Tendencia"
        />
      ) : null}
    </>
  );
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {content}
      </button>
    );
  }
  return <article className={className}>{content}</article>;
}

// ---------------------------------------------------------------------------
// Acciones panel — what your agents are proposing, what's pending decision,
// and what they already executed. Merges three previously-scattered surfaces
// (Plan de hoy, Para aprobar, Acciones de tus agentes) under one tab with a
// summary strip at the top.
// ---------------------------------------------------------------------------
function AccionesPanel({
  data,
  openAgentChat,
}: {
  data: BootstrapPayload;
  openAgentChat: (id: string) => void;
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasHiredAgents = data.summary.hired_agents > 0;

  // Full recommendation list (every state) — the board, the focus strip and the
  // list view all read this ONE query. The bootstrap `plan_today` (top-5 open)
  // seeds the first paint so the board isn't briefly empty on a cold open; the
  // query result (every state, up to 100) replaces it once loaded.
  const recQuery = useQuery({
    queryKey: REC_LIST_QUERY_KEY,
    queryFn: () => fetchRecommendations({ state: "all", limit: 100 }),
    placeholderData: (prev) => prev,
  });
  const recs = recQuery.data?.results ?? data.plan_today ?? [];

  const openRecs = recs.filter(
    (r) => r.state === "todo" || r.state === "in_progress",
  ).length;
  const inProgressRecs = recs.filter((r) => r.state === "in_progress").length;
  const todoRecs = recs.filter((r) => r.state === "todo").length;
  const doneRecs = recs.filter((r) => r.state === "done").length;
  const actionsExecuted7d = (data.recent_activity ?? []).filter((row) =>
    row.kind.startsWith("action_executed"),
  ).length;

  // View: board (default) | list. Persisted in the URL (`?view=lista`) so a
  // refresh / shared link keeps the surface, and the retired /plan deep links
  // (`?agent=`, `?rec=`) land on the board here.
  const view: "tablero" | "lista" =
    searchParams.get("view") === "lista" ? "lista" : "tablero";
  const setView = (next: "tablero" | "lista") => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "tablero") p.delete("view");
        else p.set("view", next);
        return p;
      },
      { replace: true },
    );
  };

  // Filter state — agent + plan state + time window + keyword. Agent + focus
  // target seed from the URL so `/agents/:id` deep links and the focus strip's
  // "ver en el tablero" work.
  const [agentFilter, setAgentFilter] = useState(() => searchParams.get("agent") ?? "");
  const [planStateFilter, setPlanStateFilter] = useState<
    "all" | "todo" | "in_progress" | "done" | "wont_do"
  >("all");
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  const [query, setQuery] = useState("");
  const [focusedRecId, setFocusedRecId] = useState<string | null>(
    () => searchParams.get("rec"),
  );

  // Per-installation accent map for the board + focus cards (same `--card-accent`
  // rule the kanban used).
  const agentAccents = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of data.agents) {
      if (a.accent_color_hex && a.id) m.set(a.id, a.accent_color_hex);
    }
    return m;
  }, [data.agents]);

  // Which originating insights are in today's live feed — the board card's "ver
  // el insight completo" link only renders for these (so it never dead-ends).
  const liveInsightIds = useMemo(
    () => new Set((data.today ?? []).map((o) => o.id)),
    [data.today],
  );

  const q = query.trim().toLowerCase();

  const filteredApprovals = data.approvals.filter((approval) => {
    if (agentFilter && approval.agent.id !== agentFilter) return false;
    if (!withinTimeWindow(approval.created_at, timeFilter)) return false;
    if (q) {
      const hay = `${approval.agent.name} ${approval.action.action_type} ${approval.action.expected_impact_es ?? ""} ${approval.action.guardrail_es ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Agent + time + keyword apply to every rec view; the plan-state chip is a
  // list-only refinement (the board's columns ARE the states, so it ignores it).
  // The time window only filters TERMINAL rows (when was it closed) — open
  // todo/in_progress work is always relevant and must never be hidden by a
  // "última hora / hoy" window (that would silently empty the board's columns
  // and the destacadas of perfectly valid pending tasks).
  const matchesCommon = (row: PlanTodayRow): boolean => {
    if (agentFilter && row.agent.installation_id !== agentFilter) return false;
    const isTerminalRow = row.state === "done" || row.state === "wont_do";
    if (
      isTerminalRow &&
      row.state_changed_at &&
      !withinTimeWindow(row.state_changed_at, timeFilter)
    ) {
      return false;
    }
    if (q) {
      const hay = `${row.title_es} ${row.body_es ?? ""} ${row.agent.display_name_es}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  };
  const boardRecs = recs.filter(matchesCommon);
  const filteredRecs = boardRecs.filter(
    (row) => planStateFilter === "all" || row.state === planStateFilter,
  );
  // Destacadas — the open subset, already priority-ordered by the backend.
  const focusRecs = boardRecs.filter(
    (r) => r.state === "todo" || r.state === "in_progress",
  );

  const filteredActivity = (data.recent_activity ?? []).filter((row) => {
    if (agentFilter && row.agent.id !== agentFilter) return false;
    if (!withinTimeWindow(row.occurred_at, timeFilter)) return false;
    if (q) {
      const hay = `${row.headline_es ?? ""} ${row.subtitle_es ?? ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const hasAnyFilter =
    agentFilter !== "" ||
    planStateFilter !== "all" ||
    timeFilter !== "all" ||
    q !== "";

  const clearFilters = () => {
    setAgentFilter("");
    setPlanStateFilter("all");
    setTimeFilter("all");
    setQuery("");
  };

  // Focus a rec from the destacadas strip: make sure we're on the board, then
  // hand the highlight target to <ActionsBoard/> (it scrolls + pulses).
  const focusRec = (recId: string) => {
    if (view !== "tablero") setView("tablero");
    setFocusedRecId(recId);
  };

  return (
    <div
      className="accionesPanel"
      aria-label="Acciones y aprobaciones"
      data-testid="dashboard-acciones-panel"
    >
      {/* L147 Bruno — tab-level orientation eyebrow (DESIGN.md §11.1 parity
          contract). Same primitive as Stats so all five tabs open with the
          same type signal. */}
      <DashboardTabEyebrow
        eyebrow="Para decidir"
        helper="Tu equipo dejó esto listo para que decidas vos."
        testId="acciones-tab-eyebrow"
      />

      {/* Destacadas — the 3 most important open recs, so the merchant always
          knows where to focus next, regardless of board/list view. */}
      <ActionsFocusStrip
        recs={focusRecs}
        agentAccents={agentAccents}
        onFocusRec={focusRec}
      />

      <div className="accionesSummary">
        {/* "Para aprobar" + "Ejecutadas" are actor-class stats — in the
            observer/analyst-only v1 roster they're structurally 0, so a
            permanent "0 pendientes" tile reads as broken. They render only
            when an agent actually produces approvals / executions; the two
            stats that genuinely move (Por arrancar / En curso) plus the
            progress stat (Hechas) always anchor the strip. */}
        {data.approvals.length > 0 ? (
          <AccionesSummaryTile
            label="Para aprobar"
            value={data.approvals.length}
            unit={data.approvals.length === 1 ? "pendiente" : "pendientes"}
            tone="warning"
          />
        ) : null}
        <AccionesSummaryTile
          label="Por arrancar"
          value={todoRecs}
          unit={todoRecs === 1 ? "recomendación" : "recomendaciones"}
          tone={todoRecs > 0 ? "primary" : "neutral"}
        />
        <AccionesSummaryTile
          label="En curso"
          value={inProgressRecs}
          unit={inProgressRecs === 1 ? "iniciada por vos" : "iniciadas por vos"}
          tone="accent"
        />
        <AccionesSummaryTile
          label="Hechas"
          value={doneRecs}
          unit={doneRecs === 1 ? "completada" : "completadas"}
          tone={doneRecs > 0 ? "accent" : "neutral"}
        />
        {actionsExecuted7d > 0 ? (
          <AccionesSummaryTile
            label="Ejecutadas (7d)"
            value={actionsExecuted7d}
            unit={actionsExecuted7d === 1 ? "acción de tus agentes" : "acciones de tus agentes"}
            tone="accent"
          />
        ) : null}
      </div>

      {/* Toolbar — keyword search (grows) + the board⇄list view toggle, pinned
          to the right of the same row so the toggle no longer floats alone on
          its own line. The kanban that used to live at its own /plan page is
          this tab's default surface. */}
      <div className="accionesToolbar" data-testid="acciones-toolbar">
        <div className="analisisSearch accionesToolbar__search">
          <input
            type="search"
            placeholder="Buscar por palabra clave en las acciones…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Buscar acciones"
            data-testid="acciones-search"
          />
        </div>
        <div
          className="accionesViewToggle"
          role="tablist"
          aria-label="Cómo ver tus acciones"
          data-testid="acciones-view-toggle"
        >
          <button
            type="button"
            role="tab"
            aria-selected={view === "tablero"}
            className={`accionesViewToggle__btn${view === "tablero" ? " accionesViewToggle__btn--active" : ""}`}
            onClick={() => setView("tablero")}
            data-testid="acciones-view-tablero"
          >
            Tablero
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={view === "lista"}
            className={`accionesViewToggle__btn${view === "lista" ? " accionesViewToggle__btn--active" : ""}`}
            onClick={() => setView("lista")}
            data-testid="acciones-view-lista"
          >
            Lista
          </button>
        </div>
      </div>

      {/* L146 Lucía — filters bar. The plan-state chips only show in the list
          view (the board's columns already split by state). */}
      <div
        className="dashboardFiltersGroup"
        data-testid="acciones-filters-group"
      >
        <AccionesFiltersBar
          agents={data.agents}
          agentFilter={agentFilter}
          planStateFilter={planStateFilter}
          timeFilter={timeFilter}
          showStateFilter={view === "lista"}
          onAgentChange={setAgentFilter}
          onStateChange={setPlanStateFilter}
          onTimeChange={setTimeFilter}
          hasAnyFilter={hasAnyFilter}
          onClear={clearFilters}
        />
      </div>

      {!hasHiredAgents ? (
        <DashboardEmpty
          title="Todavía no tenés acciones"
          body="Contratá tu primer agente para que empiece a leer tu tienda y proponga acciones concretas."
          actionLabel="Conocer al equipo"
          onAction={() => navigate("/equipo")}
        />
      ) : (
        <>
          {/* Para aprobar — shared above both views (rare + important; it isn't a
              board column). */}
          {filteredApprovals.length > 0 ? (
            <Panel
              title="Para aprobar"
              subtitle={`${filteredApprovals.length} ${
                filteredApprovals.length === 1
                  ? "acción esperando tu decisión"
                  : "acciones esperando tu decisión"
              }`}
            >
              <div className="stack">
                {filteredApprovals.map((approval) => (
                  <ApprovalCard
                    key={approval.id}
                    approval={approval}
                    onOpen={() => navigate(`/approvals/${approval.id}`)}
                    onAgentClick={() => openAgentChat(approval.agent.id)}
                  />
                ))}
              </div>
            </Panel>
          ) : null}

          {recQuery.isError ? (
            <DashboardEmpty
              title="No pudimos cargar tus acciones"
              body="Hubo un problema al traer las recomendaciones de tus agentes. Probá recargar el tablero."
            />
          ) : view === "tablero" ? (
            recQuery.isLoading && !recQuery.data ? (
              <div
                className="actionsBoard__loading"
                role="status"
                aria-live="polite"
                data-testid="acciones-board-loading"
              >
                Cargando tus acciones…
              </div>
            ) : boardRecs.length === 0 && hasAnyFilter && recs.length > 0 ? (
              <DashboardEmpty
                title="Sin acciones para los filtros activos"
                body="Ajustá los filtros para ver lo que tu equipo está moviendo en otras ventanas."
                actionLabel="Limpiar filtros"
                onAction={clearFilters}
              />
            ) : (
              <ActionsBoard
                recs={boardRecs}
                agentAccents={agentAccents}
                liveInsightIds={liveInsightIds}
                onOpenInsight={(id) => navigate(`/analisis?insight=${id}`)}
                highlightRecId={focusedRecId}
              />
            )
          ) : filteredRecs.length === 0 ? (
            hasAnyFilter && recs.length > 0 ? (
              <DashboardEmpty
                title="Sin acciones para los filtros activos"
                body="Ajustá los filtros para ver lo que tu equipo está moviendo en otras ventanas."
                actionLabel="Limpiar filtros"
                onAction={clearFilters}
              />
            ) : (
              <DashboardEmpty
                title="Tu equipo todavía no propuso acciones"
                body="Cuando los agentes terminen su próxima corrida, las recomendaciones que valga la pena tomar van a aparecer acá."
                actionLabel="Hablá con un agente"
                onAction={() => navigate("/chats")}
              />
            )
          ) : (
            <PlanTodayPanel
              rows={filteredRecs}
              hasHiredAgents={hasHiredAgents}
              total={hasAnyFilter ? filteredRecs.length : openRecs}
              onSeeAll={() => setView("tablero")}
            />
          )}

          {/* "Lo que tu equipo ya hizo" — executed-action history, shared below
              BOTH the board and the list so the default board view no longer
              hides it. Observer/analyst agents rarely populate it in v1, so it
              only renders when there's something to show. */}
          {filteredActivity.length > 0 ? (
            <RecentActivityPanel
              rows={filteredActivity}
              agents={data.agents}
              onAgentClick={openAgentChat}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * DashboardEmpty — shared empty-state card used across Acciones and Análisis
 * so the merchant sees the same visual rhythm regardless of which tab is
 * empty. One title, one explanation, one CTA. Variants are encoded by the
 * `tone` prop only (color), never by structure — copy carries the meaning.
 */
function DashboardEmpty({
  title,
  body,
  actionLabel,
  onAction,
}: {
  title: string;
  body: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <article className="dashboardEmpty" data-testid="dashboard-empty">
      <h3 className="dashboardEmpty__title">{title}</h3>
      <p className="dashboardEmpty__body">{body}</p>
      {actionLabel && onAction ? (
        <Button
          appearance="neutral"
          className="dashboardEmpty__cta"
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      ) : null}
    </article>
  );
}

function AccionesFiltersBar({
  agents,
  agentFilter,
  planStateFilter,
  timeFilter,
  showStateFilter = true,
  onAgentChange,
  onStateChange,
  onTimeChange,
  hasAnyFilter,
  onClear,
}: {
  agents: AgentInstallation[];
  agentFilter: string;
  planStateFilter: "all" | "todo" | "in_progress" | "done" | "wont_do";
  timeFilter: TimeFilter;
  // The plan-state chips only make sense in the list view — the board's columns
  // already split by state. Hidden (and not applied) on the board.
  showStateFilter?: boolean;
  onAgentChange: (id: string) => void;
  onStateChange: (
    state: "all" | "todo" | "in_progress" | "done" | "wont_do",
  ) => void;
  onTimeChange: (t: TimeFilter) => void;
  hasAnyFilter: boolean;
  onClear: () => void;
}) {
  const activeAgents = agents.filter((a) => a.lifecycle_state === "active");
  const stateOptions: Array<{
    key: "all" | "todo" | "in_progress" | "done" | "wont_do";
    label: string;
  }> = [
    { key: "all", label: "Todas" },
    { key: "todo", label: "Por arrancar" },
    { key: "in_progress", label: "En curso" },
    { key: "done", label: "Hechas" },
    { key: "wont_do", label: "Descartadas" },
  ];

  return (
    <div className="hoyFiltersBar" data-testid="acciones-filters-bar">
      <div className="hoyFiltersBar__header">
        <span className="hoyFiltersBar__eyebrow">Filtrar acciones</span>
        <p className="hoyFiltersBar__helper">
          Acotá la vista sin perder contexto del resto del equipo.
        </p>
        {hasAnyFilter ? (
          <button
            type="button"
            className="filterChip filterChip--clear"
            onClick={onClear}
          >
            Limpiar filtros
          </button>
        ) : null}
      </div>
      <TimeFilterRow value={timeFilter} onChange={onTimeChange} />
      {showStateFilter ? (
        <div
          className="hoyFiltersBar__row"
          role="group"
          aria-label="Filtrar por estado"
        >
          {stateOptions.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`filterChip${planStateFilter === opt.key ? " filterChip--active" : ""}`}
              onClick={() => onStateChange(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
      {activeAgents.length > 1 ? (
        <div
          className="hoyFiltersBar__row"
          role="group"
          aria-label="Filtrar por agente"
        >
          <button
            type="button"
            className={`filterChip${agentFilter === "" ? " filterChip--active" : ""}`}
            onClick={() => onAgentChange("")}
          >
            Todo el equipo
          </button>
          {activeAgents.map((agent) => (
            <button
              key={agent.id}
              type="button"
              className={`filterChip${agentFilter === agent.id ? " filterChip--active" : ""}`}
              onClick={() => onAgentChange(agent.id)}
            >
              {agent.display_name || agent.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared time-window filter — used by Acciones + Análisis.
// `"all"` is the merchant-default; the relative windows are calendar-based
// for `"today"` (start-of-day in the browser tz) and pure-elapsed for the
// rest, so "última hora" reads as "60 minutos atrás" instead of "since
// 00:00 today". Names match how a merchant would speak about the choices.
// ---------------------------------------------------------------------------
export type TimeFilter =
  | "all"
  | "last_hour"
  | "today"
  | "last_24h"
  | "last_7d";

const TIME_FILTER_OPTIONS: Array<{ key: TimeFilter; label: string }> = [
  { key: "all", label: "Todos" },
  { key: "last_hour", label: "Última hora" },
  { key: "today", label: "Hoy" },
  { key: "last_24h", label: "Últimas 24 h" },
  { key: "last_7d", label: "Últimos 7 días" },
];

function withinTimeWindow(
  iso: string,
  window: TimeFilter,
  now: Date = new Date(),
): boolean {
  if (window === "all") return true;
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return false;
  const ageMs = now.getTime() - t;
  switch (window) {
    case "last_hour":
      return ageMs >= 0 && ageMs < 60 * 60 * 1000;
    case "today": {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      return t >= start.getTime();
    }
    case "last_24h":
      return ageMs >= 0 && ageMs < 24 * 60 * 60 * 1000;
    case "last_7d":
      return ageMs >= 0 && ageMs < 7 * 24 * 60 * 60 * 1000;
  }
}

function TimeFilterRow({
  value,
  onChange,
}: {
  value: TimeFilter;
  onChange: (t: TimeFilter) => void;
}) {
  return (
    <div
      className="hoyFiltersBar__row"
      role="group"
      aria-label="Filtrar por tiempo"
      data-testid="time-filter-row"
    >
      <span className="hoyFiltersBar__rowLabel">Cuándo</span>
      {TIME_FILTER_OPTIONS.map((opt) => (
        <button
          key={opt.key}
          type="button"
          className={`filterChip${value === opt.key ? " filterChip--active" : ""}`}
          onClick={() => onChange(opt.key)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function AccionesSummaryTile({
  label,
  value,
  unit,
  tone,
}: {
  label: string;
  value: number;
  unit: string;
  tone: "accent" | "primary" | "warning" | "neutral";
}) {
  return (
    <article className={`accionesSummary__tile accionesSummary__tile--${tone}`}>
      <span className="accionesSummary__label">{label}</span>
      <span className="accionesSummary__value">{value}</span>
      <span className="accionesSummary__unit">{unit}</span>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Análisis panel — every hallazgo today, with severity + agent + keyword
// filters. Owns its own filter state so it resets cleanly each time the tab
// is opened.
// ---------------------------------------------------------------------------
function AnalisisPanel({
  data,
  agentsById,
  firstActiveAgent,
  firstInactiveAgent,
  hasHiredAgents,
  openAgentChat,
}: {
  data: BootstrapPayload;
  agentsById: Map<string, AgentInstallation>;
  firstActiveAgent: AgentInstallation | null;
  firstInactiveAgent: AgentInstallation | null;
  hasHiredAgents: boolean;
  openAgentChat: (id: string) => void;
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightInsightId = searchParams.get("insight");
  const [severityFilter, setSeverityFilter] = useState<
    "all" | "critical" | "warn" | "info"
  >("all");
  const [agentFilter, setAgentFilter] = useState("");
  const [query, setQuery] = useState("");
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  // The chronological "Lo demás de hoy" segment is capped by default so the
  // merchant isn't dropped into a wall of cards; the 3 destacados above it
  // always show in full. "Ver todas (N) →" expands the rest inline.
  const [showAll, setShowAll] = useState(false);
  const ANALISIS_REST_LIMIT = 4;

  // Deep link from an action card's "ver el insight completo" (`/analisis?insight=<id>`):
  // expand the feed so the target is rendered, then scroll to it and pulse it.
  useEffect(() => {
    if (!highlightInsightId) return;
    // Expand the feed first so a target below the default fold is rendered,
    // then defer the scroll/highlight a tick so React has painted the row.
    setShowAll(true);
    const raf = window.setTimeout(() => {
      const el = document.querySelector(`[data-obs-id="${highlightInsightId}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("workItem--highlight");
      window.setTimeout(() => el.classList.remove("workItem--highlight"), 2400);
    }, 120);
    return () => window.clearTimeout(raf);
  }, [highlightInsightId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data.today.filter((item) => {
      if (severityFilter !== "all" && item.severity !== severityFilter)
        return false;
      if (agentFilter && item.agent_installation_id !== agentFilter)
        return false;
      if (!withinTimeWindow(item.created_at, timeFilter)) return false;
      if (q) {
        const hay = `${item.headline} ${item.body}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data.today, severityFilter, agentFilter, query, timeFilter]);

  // Triage split (2026-06-21 redesign): lift the 3 most important findings into
  // a featured "Empezá por acá" segment so the merchant always knows what to
  // read first, then list everything else newest-first. Both segments hang off
  // one continuous timeline rail. Below 3 findings there's nothing to triage —
  // skip the split and render a plain chronological timeline.
  const useDestacados = filtered.length >= 3;
  const destacados = useMemo(
    () =>
      useDestacados
        ? [...filtered].sort(compareInsightImportance).slice(0, 3)
        : [],
    [filtered, useDestacados],
  );
  const rest = useMemo(() => {
    const destacadoIds = new Set(destacados.map((d) => d.id));
    return (
      useDestacados
        ? filtered.filter((item) => !destacadoIds.has(item.id))
        : [...filtered]
    ).sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [filtered, destacados, useDestacados]);
  const visibleRest = showAll ? rest : rest.slice(0, ANALISIS_REST_LIMIT);
  const hiddenCount = rest.length - visibleRest.length;

  // L147 Bruno — the redundant "Hoy" h2 stays out; the tab-level eyebrow
  // below replaces it with a parity-matching orientation label so this tab
  // opens with the same type signal as Stats / Acciones / Fuentes / Equipo
  // (DESIGN.md §11.1 parity contract).
  return (
    <section
      className="feedPanel analisisPanel--bare"
      aria-label="Hallazgos de hoy"
    >
      <DashboardTabEyebrow
        eyebrow="Hallazgos de hoy"
        helper="Lo que tu equipo encontró hoy y no se te puede pasar."
        testId="analisis-tab-eyebrow"
      />
      {data.today.length > 0 && (
        <div
          className="dashboardFiltersGroup"
          data-testid="analisis-filters-group"
        >
          <HoyFiltersBar
            agents={data.agents.filter((a) => a.lifecycle_state === "active")}
            severityFilter={severityFilter}
            agentFilter={agentFilter}
            timeFilter={timeFilter}
            onSeverity={setSeverityFilter}
            onAgent={setAgentFilter}
            onTime={setTimeFilter}
            filteredCount={filtered.length}
            totalCount={data.today.length}
          />
          <div className="analisisSearch">
            <input
              type="search"
              placeholder="Buscar por palabra clave en los hallazgos…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Buscar hallazgos"
              data-testid="analisis-search"
            />
          </div>
        </div>
      )}
      {filtered.length > 0 ? (
        <InsightTimeline
          destacados={destacados}
          rest={visibleRest}
          restTotal={rest.length}
          hiddenCount={hiddenCount}
          showAll={showAll}
          restLimit={ANALISIS_REST_LIMIT}
          agentsById={agentsById}
          openAgentChat={openAgentChat}
          onSeeAll={() => setShowAll(true)}
          onSeeLess={() => setShowAll(false)}
        />
      ) : data.today.length > 0 ? (
        <DashboardEmpty
          title="Sin hallazgos para los filtros activos"
          body={`Tus agentes encontraron ${data.today.length} ${data.today.length === 1 ? "cosa" : "cosas"} hoy — ajustá los filtros para verlas.`}
          actionLabel="Limpiar filtros"
          onAction={() => {
            setSeverityFilter("all");
            setAgentFilter("");
            setQuery("");
            setTimeFilter("all");
          }}
        />
      ) : hasHiredAgents ? (
        firstActiveAgent ? (
          <DashboardEmpty
            title="Todavía no hay hallazgos hoy"
            body="Tus agentes están en seguimiento. Cuando terminen su próxima corrida, los hallazgos van a aparecer acá."
            onAction={() =>
              navigate(`/agents/${firstActiveAgent.id}?run=1`)
            }
            actionLabel="Correr análisis ahora"
          />
        ) : (
          <DashboardEmpty
            title="Tu equipo está en pausa"
            body="Ninguno de tus agentes está activo. Reactivá al menos uno para que vuelva a observar tu tienda."
            onAction={
              firstInactiveAgent
                ? () => navigate(`/agents/${firstInactiveAgent.id}/configure`)
                : undefined
            }
            actionLabel={firstInactiveAgent ? "Reactivar agente" : undefined}
          />
        )
      ) : (
        <DashboardEmpty
          title="Todavía no hay hallazgos"
          body="Cuando un agente termine una corrida, sus hallazgos van a aparecer acá con citas a TiendaNube y las fuentes conectadas."
          onAction={() => navigate("/equipo")}
          actionLabel="Contratar mi primer agente"
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Análisis timeline (2026-06-21 redesign)
//
// The Análisis tab renders today's findings as ONE continuous left-rail
// timeline. The 3 most important findings are lifted to the top into a featured
// "Empezá por acá" segment (so the merchant always knows what to read first);
// everything else flows below in chronological order. Both segments hang off
// the same rail, giving the feed a single guided spine instead of a flat wall
// of cards.
// ---------------------------------------------------------------------------

// Severity is the dominant importance signal, then whether the finding spawned
// an action (actionable > informational), then the agent's confidence, then
// recency as the tiebreaker. Used only to pick + order the top-3 destacados.
const INSIGHT_SEVERITY_RANK: Record<WorkItem["severity"], number> = {
  critical: 3,
  warn: 2,
  info: 1,
};
const INSIGHT_CONFIDENCE_RANK: Record<string, number> = {
  alta: 3,
  media: 2,
  baja: 1,
  datos_parciales: 0,
  sin_datos_suficientes: 0,
};
function compareInsightImportance(a: WorkItem, b: WorkItem): number {
  const sev =
    (INSIGHT_SEVERITY_RANK[b.severity] ?? 1) -
    (INSIGHT_SEVERITY_RANK[a.severity] ?? 1);
  if (sev !== 0) return sev;
  const act =
    ((b.recommendations?.length ?? 0) > 0 ? 1 : 0) -
    ((a.recommendations?.length ?? 0) > 0 ? 1 : 0);
  if (act !== 0) return act;
  const conf =
    (INSIGHT_CONFIDENCE_RANK[(b.confidence || "").trim().toLowerCase()] ?? 1) -
    (INSIGHT_CONFIDENCE_RANK[(a.confidence || "").trim().toLowerCase()] ?? 1);
  if (conf !== 0) return conf;
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

function InsightTimeline({
  destacados,
  rest,
  restTotal,
  hiddenCount,
  showAll,
  restLimit,
  agentsById,
  openAgentChat,
  onSeeAll,
  onSeeLess,
}: {
  destacados: WorkItem[];
  rest: WorkItem[];
  restTotal: number;
  hiddenCount: number;
  showAll: boolean;
  restLimit: number;
  agentsById: Map<string, AgentInstallation>;
  openAgentChat: (id: string) => void;
  onSeeAll: () => void;
  onSeeLess: () => void;
}) {
  // Continuous stagger index across both segments so the whole timeline rises
  // in as one sequence on mount.
  let revealIndex = 0;
  return (
    <div className="insightTimeline" data-testid="analisis-timeline">
      <span className="insightTimeline__rail" aria-hidden="true" />
      {destacados.length > 0 ? (
        <div className="insightTimeline__segment insightTimeline__segment--featured">
          <TimelineSegmentLabel
            variant="featured"
            title="Empezá por acá"
            hint={
              destacados.length === 1
                ? "El hallazgo que no se te puede pasar"
                : `Los ${destacados.length} hallazgos que no se te pueden pasar`
            }
          />
          {destacados.map((item, i) => (
            <TimelineEntry
              key={item.id}
              item={item}
              agentsById={agentsById}
              openAgentChat={openAgentChat}
              featured
              rank={i + 1}
              revealIndex={revealIndex++}
            />
          ))}
        </div>
      ) : null}
      {rest.length > 0 ? (
        <div className="insightTimeline__segment">
          {destacados.length > 0 ? (
            <TimelineSegmentLabel
              variant="rest"
              title="Lo demás de hoy"
              hint="En orden, lo más reciente primero"
            />
          ) : null}
          {rest.map((item) => (
            <TimelineEntry
              key={item.id}
              item={item}
              agentsById={agentsById}
              openAgentChat={openAgentChat}
              revealIndex={revealIndex++}
            />
          ))}
          {hiddenCount > 0 ? (
            <div className="insightTimeline__more">
              <button
                type="button"
                className="planTodayPanel__seeAll"
                onClick={onSeeAll}
                data-testid="analisis-see-all"
              >
                Ver todas ({restTotal}) →
              </button>
            </div>
          ) : showAll && restTotal > restLimit ? (
            <div className="insightTimeline__more">
              <button
                type="button"
                className="planTodayPanel__seeAll"
                onClick={onSeeLess}
              >
                Ver menos ↑
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function TimelineSegmentLabel({
  variant,
  title,
  hint,
}: {
  variant: "featured" | "rest";
  title: string;
  hint: string;
}) {
  return (
    <div
      className={`insightTimeline__segmentLabel insightTimeline__segmentLabel--${variant}`}
    >
      <span className="insightTimeline__segmentMarker" aria-hidden="true" />
      <span className="insightTimeline__segmentTitle">{title}</span>
      <span className="insightTimeline__segmentHint">{hint}</span>
    </div>
  );
}

function TimelineEntry({
  item,
  agentsById,
  openAgentChat,
  featured = false,
  rank,
  revealIndex,
}: {
  item: WorkItem;
  agentsById: Map<string, AgentInstallation>;
  openAgentChat: (id: string) => void;
  featured?: boolean;
  rank?: number;
  revealIndex: number;
}) {
  const agent = item.agent_installation_id
    ? agentsById.get(item.agent_installation_id) ?? null
    : null;
  const accent =
    agent?.accent_color_hex ||
    item.agent_accent_color_hex ||
    DEFAULT_AGENT_ACCENT;
  return (
    <div
      className={`insightTimeline__entry severity-${item.severity}${
        featured ? " insightTimeline__entry--featured" : ""
      }`}
      style={
        {
          ["--card-accent" as string]: accent,
          ["--reveal-index" as string]: revealIndex,
        } as React.CSSProperties
      }
    >
      <span className="insightTimeline__node" aria-hidden="true">
        {featured && rank ? (
          <span className="insightTimeline__rank">{rank}</span>
        ) : null}
      </span>
      <div className="insightTimeline__cardWrap">
        <WorkItemCard item={item} agent={agent} onAgentClick={openAgentChat} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fuentes panel — connector inventory with per-source extraction stats.
// Counts how many hired agents depend on each source (capability slot) and
// surfaces the contribution to today's citations when the citation payload
// includes a `source_kind` hint.
// ---------------------------------------------------------------------------
function FuentesPanel({
  data,
  onConnectStarted,
  onOpenPicker,
}: {
  data: BootstrapPayload;
  onConnectStarted: (kind: string) => void;
  onOpenPicker: (kind: string) => void;
}) {
  // Dependent-agent count per source kind, derived from
  // `installation.required_connector_kinds`. The roster is the source of truth
  // for which capabilities are actually being read each run.
  const dependentsByKind = new Map<string, number>();
  const dependentInstallationsByKind = new Map<string, Set<string>>();
  for (const agent of data.agents) {
    for (const kind of agent.required_connector_kinds ?? []) {
      dependentsByKind.set(kind, (dependentsByKind.get(kind) ?? 0) + 1);
      if (!dependentInstallationsByKind.has(kind)) {
        dependentInstallationsByKind.set(kind, new Set());
      }
      dependentInstallationsByKind.get(kind)!.add(agent.id);
    }
  }

  // "Hallazgos hoy" per source: count observations whose agent depends on
  // the source kind. Citation payloads don't carry a typed `source_kind`
  // today (shape varies by agent), so this aggregation goes through the
  // installation→connector binding instead — more honest than a 0-most-of-
  // the-time citation count.
  const findingsByKind = new Map<string, number>();
  for (const item of data.today) {
    const instId = item.agent_installation_id;
    if (!instId) continue;
    for (const [kind, instSet] of dependentInstallationsByKind) {
      if (instSet.has(instId)) {
        findingsByKind.set(kind, (findingsByKind.get(kind) ?? 0) + 1);
      }
    }
  }

  const totalCitations = data.today.reduce(
    (sum, item) => sum + (item.citations?.length ?? 0),
    0,
  );

  // L147 Bruno — tab-level orientation eyebrow (DESIGN.md §11.1 parity
  // contract). The inner `<FuentesSection>` h2 stays for the empty case only
  // (when sources.length === 0 we surface a richer empty surface); the
  // populated case is led by the tab eyebrow.
  return (
    <div
      className="fuentesPanel"
      aria-label="Fuentes conectadas"
      data-testid="dashboard-fuentes-panel"
    >
      <DashboardTabEyebrow
        eyebrow="Tus conexiones"
        helper="De dónde lee tu equipo y qué tan al día está cada cuenta."
        testId="fuentes-tab-eyebrow"
      />
      {(data.summary.available_sources ?? 0) > 0 ? (
        <p className="fuentesPanel__availableHint" data-testid="fuentes-available-hint">
          {data.summary.available_sources}{" "}
          {data.summary.available_sources === 1
            ? "fuente disponible"
            : "fuentes disponibles"}{" "}
          para conectar — sumá más contexto a tus agentes.
        </p>
      ) : null}
      {data.sources.length > 0 ? (
        <div className="fuentesStatsGrid">
          {data.sources.map((source) => {
            const stats = source.extraction_stats ?? [];
            const totalItems = stats.reduce((sum, s) => sum + s.items, 0);
            const totalReads = stats.reduce((sum, s) => sum + s.reads, 0);
            return (
              <FuenteStatCard
                key={source.kind}
                source={source}
                dependents={dependentsByKind.get(source.kind) ?? 0}
                findingsToday={findingsByKind.get(source.kind) ?? 0}
                totalRead7d={totalItems}
                totalReads7d={totalReads}
                onConnectStarted={onConnectStarted}
                onOpenPicker={onOpenPicker}
              />
            );
          })}
        </div>
      ) : null}

      {totalCitations > 0 ? (
        <p className="fuentesPanel__footer">
          {totalCitations} {totalCitations === 1 ? "cita" : "citas"} hoy en
          total — cada hallazgo cita la evidencia de las fuentes conectadas.
        </p>
      ) : null}

      {data.sources.length === 0 ? (
        // L147 Bruno — was a `<FuentesSection>` whose internal h2 "Fuentes
        // conectadas" duplicated the tab-level eyebrow above. SourceRack
        // alone carries the empty-state visual; the tab eyebrow already
        // orients the merchant. flat-type-hierarchy fix.
        <SourceRack sources={data.sources} />
      ) : null}
    </div>
  );
}

// Provider marks shared with SourceRack — we re-declare here so the Fuentes
// stat card can render an initials chip without importing SourceRack's
// internals. Same palette as the home rack so the merchant sees a coherent
// brand identity across the tab.
const FUENTE_PROVIDER_MARKS: Record<
  string,
  { initials: string; bg: string; fg: string; fallback: string }
> = {
  tiendanube: { initials: "TN", bg: "#00407a", fg: "#ffffff", fallback: "TiendaNube" },
  // Light tints behind the full-color Meta/GA logos (rendered via
  // providerLogoFor) so each brand mark reads cleanly on the card.
  meta_ads: { initials: "M", bg: "#e7f0fe", fg: "#1877f2", fallback: "Meta Ads" },
  google_analytics_4: {
    initials: "GA",
    bg: "#fef3da",
    fg: "#e37400",
    fallback: "Google Analytics",
  },
  google_ads: { initials: "GG", bg: "#34a853", fg: "#ffffff", fallback: "Google Ads" },
  mercadolibre: {
    initials: "ML",
    bg: "#fff159",
    fg: "#2d3277",
    fallback: "MercadoLibre",
  },
};

// Per-connector connect copy — each source reads a different kind of data, so
// the "Conectar" card must not claim "web traffic" for an ad connector.
const CONNECT_COPY_BY_KIND: Record<string, string> = {
  google_analytics_4:
    "Conectá Google Analytics para que tu equipo lea el tráfico web de tu " +
    "tienda — canales, páginas de aterrizaje, dispositivos y productos.",
  meta_ads:
    "Conectá Meta Ads para que tu equipo lea la performance de tus campañas " +
    "de Facebook e Instagram.",
};

// How each source changes what the agents can conclude — shown under the
// connect copy so the merchant understands the payoff, not just the ask.
const IMPACT_COPY_BY_KIND: Record<string, string> = {
  google_analytics_4:
    "Con esto los análisis cruzan tus ventas con el comportamiento del " +
    "visitante: de dónde llega el tráfico, qué páginas convierten y en qué " +
    "paso se cae la compra.",
  meta_ads:
    "Suma el costo y el retorno de tus campañas a cada hallazgo, para que las " +
    "recomendaciones tengan en cuenta cuánto estás pagando por venta.",
};

function FuenteStatCard({
  source,
  dependents,
  findingsToday,
  totalRead7d,
  totalReads7d,
  onConnectStarted,
  onOpenPicker,
}: {
  source: SourceState;
  dependents: number;
  findingsToday: number;
  totalRead7d: number;
  totalReads7d: number;
  onConnectStarted: (kind: string) => void;
  onOpenPicker: (kind: string) => void;
}) {
  const mark = FUENTE_PROVIDER_MARKS[source.kind] || {
    initials: source.kind.slice(0, 2).toUpperCase(),
    bg: "#1f2a3d",
    fg: "#ffffff",
    fallback: source.kind,
  };
  const name = source.display_name || mark.fallback;
  const queryClient = useQueryClient();
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  // Per-capability read breakdown is collapsed by default — the card leads with
  // the KPI tiles; "Ver más" reveals the detail on demand.
  const [showReads, setShowReads] = useState(false);
  // Disconnect is a two-step inline confirm (no modal / no browser dialog).
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  const disconnect = useMutation({
    mutationFn: () => disconnectConnector(source.kind),
    onSuccess: () => {
      setConfirmingDisconnect(false);
      void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
  });

  // Only the picker-backed connectors can be changed/disconnected from here;
  // TiendaNube is the core install.
  const isDeferred =
    source.kind === "meta_ads" || source.kind === "google_analytics_4";
  const changeLabel =
    source.kind === "google_analytics_4" ? "Cambiar propiedad" : "Cambiar cuenta";

  async function handleConnect() {
    setConnecting(true);
    setConnectError(null);
    // Register the pending connect BEFORE opening the tab so the parent's
    // focus-return refetch + picker prompt fire when the merchant comes back.
    onConnectStarted(source.kind);
    try {
      // Opens the consent screen in a new tab (escapes the TN admin iframe).
      await openConnectorOAuthTab(source.kind);
    } catch {
      setConnectError(
        "No pudimos abrir la conexión. Revisá que no se haya bloqueado la ventana y probá de nuevo.",
      );
    } finally {
      setConnecting(false);
    }
  }

  // OAuth finished but the merchant still has to bind an ad-account / property
  // before reads can run. Takes precedence over the connected KPI view so the
  // half-finished connection can't masquerade as healthy.
  if (source.needs_selection) {
    return (
      <article
        className="fuenteStatCard fuenteStatCard--warn"
        data-testid={`fuente-stat-${source.kind}`}
      >
        <header className="fuenteStatCard__head">
          <span
            className="fuenteStatCard__mark"
            style={
              {
                ["--mark-bg" as string]: mark.bg,
                ["--mark-fg" as string]: mark.fg,
              } as React.CSSProperties
            }
            aria-hidden="true"
          >
            {providerLogoFor(source.kind, 24) ?? mark.initials}
          </span>
          <div className="fuenteStatCard__identity">
            <h3 className="fuenteStatCard__name">{name}</h3>
            <p className="fuenteStatCard__meta">
              <span
                className="fuenteStatCard__dot fuenteStatCard__dot--warn"
                aria-hidden="true"
              />
              {source.kind === "google_analytics_4"
                ? "Falta elegir la propiedad"
                : "Falta elegir la cuenta"}
            </p>
          </div>
          <span
            className="fuenteStatCard__chip fuenteStatCard__chip--warn"
            role="status"
          >
            Casi lista
          </span>
        </header>
        <p className="fuenteStatCard__availableCopy">
          Conectaste {name}. Elegí{" "}
          {source.kind === "google_analytics_4"
            ? "la propiedad de Analytics"
            : "la cuenta publicitaria"}{" "}
          que querés que tu equipo lea para terminar.
        </p>
        <div className="fuenteStatCard__actions">
          <div className="fuenteStatCard__actionsRow fuenteStatCard__actionsRow--end">
            <Button
              appearance="primary"
              className="fuenteStatCard__cta"
              onClick={() => onOpenPicker(source.kind)}
            >
              {source.kind === "google_analytics_4"
                ? "Elegí tu propiedad"
                : "Elegí tu cuenta"}
            </Button>
          </div>
        </div>
      </article>
    );
  }

  // Connectable-but-not-yet-connected source (no ConnectorState row): show a
  // connect CTA instead of empty KPIs, so the merchant can link e.g. Google
  // Analytics straight from the Fuentes tab. `dependents` here is a "would
  // use" count, not a live one.
  if (source.available) {
    return (
      <article
        className="fuenteStatCard fuenteStatCard--available"
        data-testid={`fuente-stat-${source.kind}`}
      >
        <header className="fuenteStatCard__head">
          <span
            className="fuenteStatCard__mark"
            style={
              {
                ["--mark-bg" as string]: mark.bg,
                ["--mark-fg" as string]: mark.fg,
              } as React.CSSProperties
            }
            aria-hidden="true"
          >
            {providerLogoFor(source.kind, 24) ?? mark.initials}
          </span>
          <div className="fuenteStatCard__identity">
            <h3 className="fuenteStatCard__name">{name}</h3>
            <p className="fuenteStatCard__meta">
              <span
                className="fuenteStatCard__dot fuenteStatCard__dot--neutral"
                aria-hidden="true"
              />
              Disponible para conectar
            </p>
          </div>
          <span
            className="fuenteStatCard__chip fuenteStatCard__chip--neutral"
            role="status"
          >
            Sin conectar
          </span>
        </header>
        <p className="fuenteStatCard__availableCopy">
          {CONNECT_COPY_BY_KIND[source.kind] ??
            `Conectá ${name} para sumar esa fuente a los análisis de tu equipo.`}
          {dependents > 0
            ? ` ${dependents} ${
                dependents === 1 ? "agente la usaría" : "agentes la usarían"
              }.`
            : ""}
        </p>
        {IMPACT_COPY_BY_KIND[source.kind] ? (
          <p className="fuenteStatCard__impactCopy">
            {IMPACT_COPY_BY_KIND[source.kind]}
          </p>
        ) : null}
        <div className="fuenteStatCard__actions">
          <div className="fuenteStatCard__actionsRow fuenteStatCard__actionsRow--end">
            <Button
              appearance="primary"
              className="fuenteStatCard__cta"
              onClick={handleConnect}
              disabled={connecting}
            >
              {connecting ? "Abriendo…" : "Conectar"}
            </Button>
          </div>
          {connectError ? (
            <p className="fuenteStatCard__emptyExtract" role="alert">
              {connectError}
            </p>
          ) : null}
        </div>
      </article>
    );
  }
  const lastRead = source.last_success_at
    ? formatRelative(source.last_success_at)
    : "Sin lecturas";
  const stateLabel =
    source.state === "healthy" || source.state === "connected"
      ? "Conectada"
      : source.state === "expired"
        ? "Acceso vencido"
        : source.state === "error"
          ? "Con error"
          : source.state === "degraded" || source.state === "limited"
            ? "Limitada"
            : "Desconectada";
  const stateTone =
    source.state === "healthy" || source.state === "connected"
      ? "ok"
      : source.state === "expired" ||
          source.state === "degraded" ||
          source.state === "limited"
        ? "warn"
        : source.state === "error"
          ? "danger"
          : "neutral";

  // Per-capability rows want a relative bar — we anchor each row to the
  // heaviest item in this card so the bar reads as "Productos is the bulk
  // of what we pulled" rather than a per-row 0-100% rescale.
  const stats = source.extraction_stats ?? [];
  const maxItems = stats.reduce((m, s) => Math.max(m, s.items), 0);

  const locale = getLocale();
  const fmtInt = (n: number) => n.toLocaleString(locale);

  // Connected-card actions:
  //  • deferred + healthy → "Cambiar propiedad/cuenta" (reopen the picker)
  //  • not healthy (expired/error/degraded) → "Reconectar" (re-run OAuth)
  //  • deferred → always "Desconectar" (TiendaNube is the core install)
  const isHealthy = stateTone === "ok";
  const showChange = isDeferred && isHealthy;
  const showReconnect = !isHealthy;
  const showDisconnect = isDeferred;
  const hasActions = showChange || showReconnect || showDisconnect;

  return (
    <article
      className={`fuenteStatCard fuenteStatCard--${stateTone}`}
      data-testid={`fuente-stat-${source.kind}`}
    >
      <header className="fuenteStatCard__head">
        <span
          className="fuenteStatCard__mark"
          style={
            {
              ["--mark-bg" as string]: mark.bg,
              ["--mark-fg" as string]: mark.fg,
            } as React.CSSProperties
          }
          aria-hidden="true"
        >
          {providerLogoFor(source.kind, 24) ?? mark.initials}
        </span>
        <div className="fuenteStatCard__identity">
          <h3 className="fuenteStatCard__name">{name}</h3>
          <p className="fuenteStatCard__meta">
            <span
              className={`fuenteStatCard__dot fuenteStatCard__dot--${stateTone}`}
              aria-hidden="true"
            />
            {source.last_success_at ? (
              <>
                Última lectura {lastRead}
                <span className="fuenteStatCard__metaAbs">
                  · {formatDateTime(source.last_success_at)}
                </span>
              </>
            ) : (
              "Sin lecturas todavía"
            )}
          </p>
        </div>
        <span
          className={`fuenteStatCard__chip fuenteStatCard__chip--${stateTone}`}
          role="status"
        >
          {stateLabel}
        </span>
      </header>

      <div className="fuenteStatCard__kpis">
        <KpiPill label={dependents === 1 ? "agente la usa" : "agentes la usan"} value={dependents} />
        <KpiPill
          label={findingsToday === 1 ? "hallazgo hoy" : "hallazgos hoy"}
          value={findingsToday}
          tone={findingsToday > 0 ? "primary" : "neutral"}
        />
        <KpiPill
          label="registros leídos · 7d"
          value={fmtInt(totalRead7d)}
          tone={totalRead7d > 0 ? "accent" : "neutral"}
        />
        <KpiPill
          label={totalReads7d === 1 ? "lectura · 7d" : "lecturas · 7d"}
          value={totalReads7d}
        />
      </div>

      {stats.length > 0 ? (
        <div className="fuenteStatCard__reads">
          <button
            type="button"
            className="fuenteStatCard__readsToggle"
            aria-expanded={showReads}
            onClick={() => setShowReads((v) => !v)}
          >
            {showReads ? "Ocultar detalle" : "Ver detalle de lecturas"}
          </button>
          {showReads ? (
            <div className="fuenteStatCard__breakdown">
              <p className="fuenteStatCard__breakdownEyebrow">
                Lo que leyó tu equipo · últimos 7 días
              </p>
              <ul className="fuenteStatCard__capList">
                {stats.map((stat) => {
              const widthPct =
                maxItems > 0 ? Math.max(4, (stat.items / maxItems) * 100) : 0;
              return (
                <li
                  key={stat.capability_id}
                  className="fuenteStatCard__capRow"
                  title={stat.capability_id}
                >
                  <div className="fuenteStatCard__capTop">
                    <span className="fuenteStatCard__capLabel">{stat.label_es}</span>
                    <span className="fuenteStatCard__capValue">
                      <strong>{fmtInt(stat.items)}</strong>
                      <span className="fuenteStatCard__capUnit">
                        {stat.items === 1 ? "registro" : "registros"} ·{" "}
                        {stat.reads} {stat.reads === 1 ? "lectura" : "lecturas"}
                      </span>
                    </span>
                  </div>
                  <div className="fuenteStatCard__capBarWrap">
                    <span
                      className="fuenteStatCard__capBar"
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </li>
              );
                })}
              </ul>
            </div>
          ) : null}
        </div>
      ) : source.last_success_at ? (
        <p className="fuenteStatCard__emptyExtract">
          Sin lecturas detectadas en los últimos 7 días.
        </p>
      ) : null}

      {/* Manage a live source. "Cambiar propiedad/cuenta" reopens the picker
          (re-pick from the already-authorized account); "Reconectar" re-runs
          OAuth when the grant broke; "Desconectar" revokes the credential. All
          gated to the picker-backed connectors — TiendaNube is the core
          install and isn't managed here. */}
      {hasActions ? (
        <div className="fuenteStatCard__actions">
          {confirmingDisconnect ? (
            <div className="fuenteStatCard__confirm">
              <p className="fuenteStatCard__confirmText">
                ¿Desconectar {name}? Tu equipo dejará de leer esta fuente hasta
                que la vuelvas a conectar.
              </p>
              <div className="fuenteStatCard__actionsRow">
                <Button
                  appearance="neutral"
                  onClick={() => setConfirmingDisconnect(false)}
                  disabled={disconnect.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  appearance="danger"
                  onClick={() => disconnect.mutate()}
                  disabled={disconnect.isPending}
                >
                  {disconnect.isPending ? "Desconectando…" : "Sí, desconectar"}
                </Button>
              </div>
            </div>
          ) : (
            <div className="fuenteStatCard__actionsRow">
              {showChange ? (
                <Button
                  appearance="neutral"
                  onClick={() => onOpenPicker(source.kind)}
                >
                  {changeLabel}
                </Button>
              ) : null}
              {showReconnect ? (
                <Button
                  appearance="neutral"
                  onClick={handleConnect}
                  disabled={connecting}
                >
                  {connecting ? "Abriendo…" : "Reconectar"}
                </Button>
              ) : null}
              {showDisconnect ? (
                <Button
                  appearance="neutral"
                  className="fuenteStatCard__disconnectBtn"
                  onClick={() => setConfirmingDisconnect(true)}
                >
                  Desconectar
                </Button>
              ) : null}
            </div>
          )}
          {connectError ? (
            <p className="fuenteStatCard__emptyExtract" role="alert">
              {connectError}
            </p>
          ) : null}
          {disconnect.isError ? (
            <p className="fuenteStatCard__emptyExtract" role="alert">
              No pudimos desconectar la fuente. Probá de nuevo.
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function KpiPill({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number | string;
  tone?: "neutral" | "accent" | "primary";
}) {
  return (
    <div className={`fuenteStatCard__kpi fuenteStatCard__kpi--${tone}`}>
      <span className="fuenteStatCard__kpiValue">{value}</span>
      <span className="fuenteStatCard__kpiLabel">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Equipo panel — the unified roster + hire-confirmation surface. Wraps the
// existing TuEquipo component so its filters and hire modal stay intact.
// ---------------------------------------------------------------------------
function EquipoPanel({
  hiredAgents,
  recentActivity,
}: {
  hiredAgents: AgentInstallation[];
  recentActivity: RecentActivityRow[];
}) {
  // Header strip removed — the hired-agent count now lives on the "Equipo"
  // tab badge in `<DashboardTabsNav />`, so the roster reads directly
  // without a duplicate count block above it.
  return (
    <section
      className="equipoPanel"
      aria-label="Tu equipo"
      data-testid="dashboard-equipo-panel"
    >
      {/* L147 Bruno — tab-level orientation eyebrow (DESIGN.md §11.1 parity
          contract). TuEquipo's own h2 is `visuallyHidden`, so no hierarchy
          conflict — the eyebrow is the merchant's first visible signal on
          this tab. */}
      <DashboardTabEyebrow
        eyebrow="Tu equipo"
        helper="Conocé a cada agente y sumá los que tu tienda necesita."
        testId="equipo-tab-eyebrow"
      />
      <TuEquipo hiredAgents={hiredAgents} recentActivity={recentActivity} />
    </section>
  );
}

function sevenDayActivityTrend(payload: BootstrapPayload): number[] {
  const days = 7;
  const buckets: number[] = [];
  for (let i = 0; i < days; i += 1) buckets.push(0);
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  for (const row of payload.recent_activity ?? []) {
    const ts = new Date(row.occurred_at).getTime();
    if (!Number.isFinite(ts)) continue;
    const ageDays = Math.floor((now - ts) / dayMs);
    if (ageDays < 0 || ageDays >= days) continue;
    const idx = days - 1 - ageDays;
    buckets[idx] += 1;
  }
  const allZero = buckets.reduce((sum, v) => sum + v, 0) === 0;
  if (allZero && payload.summary.observations_today > 0) {
    buckets[days - 1] = payload.summary.observations_today;
  }
  return buckets;
}


// L101 — Dashboard §4: filter bar for the "Hoy" panel. Severity chips + agent
// chips (when >1 active agent). Mobile collapses each row to a horizontal-
// scrolling strip via .hoyFiltersBar__row overflow-x: auto.
type HoySeverity = "all" | "critical" | "warn" | "info";

function HoyFiltersBar({
  agents,
  severityFilter,
  agentFilter,
  timeFilter,
  onSeverity,
  onAgent,
  onTime,
  filteredCount,
  totalCount,
}: {
  agents: AgentInstallation[];
  severityFilter: HoySeverity;
  agentFilter: string;
  timeFilter: TimeFilter;
  onSeverity: (v: HoySeverity) => void;
  onAgent: (id: string) => void;
  onTime: (t: TimeFilter) => void;
  filteredCount: number;
  totalCount: number;
}) {
  const SEVERITY_OPTIONS: { key: HoySeverity; label: string }[] = [
    { key: "all", label: "Todos" },
    { key: "critical", label: "Crítico" },
    { key: "warn", label: "Advertencia" },
    { key: "info", label: "Informativo" },
  ];

  return (
    <div className="hoyFiltersBar" data-testid="hoy-filters-bar">
      <div className="hoyFiltersBar__header">
        <span className="hoyFiltersBar__eyebrow">Filtrar hoy</span>
        <p className="hoyFiltersBar__helper">
          Ajustá el feed sin salir del panel.
        </p>
        {/* L149 Nico — `aria-live="polite"` so when a merchant toggles a
            severity / agent / time chip, the new count is announced to
            screen-reader users (the visible count line and the feed below
            change in lockstep — the visible delta needs an SR equivalent
            so chip-driven filtering isn't motion-as-only-signal for AT). */}
        {filteredCount < totalCount ? (
          <p
            className="hoyFiltersBar__count"
            aria-live="polite"
            aria-atomic="true"
            data-testid="hoy-filters-count"
          >
            {filteredCount} de {totalCount} hallazgos
          </p>
        ) : null}
      </div>
      <TimeFilterRow value={timeFilter} onChange={onTime} />
      <div
        className="hoyFiltersBar__row"
        role="group"
        aria-label="Filtrar por severidad"
        data-testid="hoy-severity-chips"
      >
        {SEVERITY_OPTIONS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`filterChip${severityFilter === key ? " filterChip--active" : ""}`}
            onClick={() => onSeverity(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {agents.length > 1 && (
        <div
          className="hoyFiltersBar__row"
          role="group"
          aria-label="Filtrar por agente"
          data-testid="hoy-agent-chips"
        >
          <button
            type="button"
            className={`filterChip${agentFilter === "" ? " filterChip--active" : ""}`}
            onClick={() => onAgent("")}
          >
            Todos los agentes
          </button>
          {agents.map((agent) => (
            <button
              key={agent.id}
              type="button"
              className={`filterChip${agentFilter === agent.id ? " filterChip--active" : ""}`}
              onClick={() => onAgent(agent.id)}
            >
              {agent.display_name || agent.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FuentesSection({ sources }: { sources: import("./types").SourceState[] }) {
  return (
    <section
      className="sectionEditorial"
      aria-label="Fuentes conectadas"
      data-testid="fuentes-rack"
    >
      <div className="sectionEditorial__header">
        <h2 className="sectionEditorial__title">Fuentes conectadas</h2>
        <p className="sectionEditorial__subtitle">
          Lo que tu equipo puede leer hoy.
        </p>
      </div>
      <SourceRack sources={sources} />
    </section>
  );
}

function BrandVoiceCloser() {
  return (
    <section className="brandVoiceCloser" aria-label="Cierre">
      <p>{BRAND_VOICE_CLOSER_ES}</p>
    </section>
  );
}

function RecentActivityPanel({
  rows,
  agents,
  onAgentClick,
}: {
  rows: RecentActivityRow[];
  agents: AgentInstallation[];
  onAgentClick?: (installationId: string) => void;
}) {
  const navigate = useNavigate();
  const visible = rows.slice(0, 8);
  const hasActorCapableAgent = agents.some((agent) =>
    isActorCapableAutonomy(agent.autonomy_class),
  );
  return (
    <section
      className="sectionEditorial recentActivityPanel"
      aria-label="Acciones de tus agentes"
    >
      <div className="sectionEditorial__header">
        <div>
          <h2 className="sectionEditorial__title">Acciones de tus agentes</h2>
          <p className="sectionEditorial__subtitle">
            Cambios que ejecutaron en tu tienda — pausas, ajustes de
            presupuesto, respuestas a reseñas — con su resultado.
          </p>
        </div>
      </div>
      {visible.length > 0 ? (
        <ul className="recentActivity">
          {visible.map((row) => (
            <RecentActivityRowItem
              key={row.id}
              row={row}
              onOpen={
                row.approval_id
                  ? () => navigate(`/approvals/${row.approval_id}`)
                  : undefined
              }
              onAgentClick={
                onAgentClick && row.agent.id
                  ? () => onAgentClick(row.agent.id)
                  : undefined
              }
            />
          ))}
        </ul>
      ) : (
        <EmptyState
          title={
            hasActorCapableAgent
              ? "Tus agentes todavía no actuaron"
              : "Tu equipo todavía está en modo análisis"
          }
          body={
            hasActorCapableAgent
              ? "Esta sección registra acciones — cuando un agente pause una campaña, ajuste un presupuesto o responda una reseña en tu tienda, va a aparecer acá con el detalle de qué cambió."
              : "Los agentes activos observan, explican y recomiendan; no ejecutan cambios automáticos en v1. Para pedir contexto o una lectura más profunda, abrí la conversación con tu equipo."
          }
          onAction={
            hasActorCapableAgent || agents.length === 0
              ? undefined
              : () => navigate("/chats")
          }
          actionLabel={
            hasActorCapableAgent || agents.length === 0
              ? undefined
              : "Conversar con mi equipo"
          }
        />
      )}
    </section>
  );
}

function isActorCapableAutonomy(autonomyClass: string | undefined): boolean {
  if (!autonomyClass) return false;
  const normalized = autonomyClass.toLowerCase();
  return (
    normalized.includes("actor") ||
    normalized.includes("propose") ||
    normalized.includes("autonomous")
  );
}

function Panel({
  title,
  subtitle,
  className = "",
  children,
}: {
  title: string;
  subtitle?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={`sectionEditorial ${className}`}>
      <div className="sectionEditorial__header">
        <div>
          <h2 className="sectionEditorial__title">{title}</h2>
          {subtitle ? (
            <p className="sectionEditorial__subtitle">{subtitle}</p>
          ) : null}
        </div>
      </div>
      {children}
    </section>
  );
}

// `confidenceChipFor` moved to `./confidenceChip` in L75 so AgentHistory and
// AgentInsights can reuse the same Hoy-card mapping for the 2026-05-12
// anti-silence confidence values.

// The Análisis-feed insight card is the shared `InsightCard` (variant="feed");
// this wrapper derives the agent handle + accent and supplies the collapsed
// "N acciones recomendadas" disclosure as the recommendations slot.
function WorkItemCard({
  item,
  agent,
  onAgentClick,
}: {
  item: WorkItem;
  agent: AgentInstallation | null;
  onAgentClick?: (installationId: string) => void;
}) {
  // Prefer the hired-roster identity; fall back to the row's own agent fields
  // so a sample/PREVIEW finding (whose install isn't on the roster) still shows
  // the agent's casual name + avatar + accent instead of the role + a letter.
  const rosterFirstName =
    agent?.display_name?.trim() || item.agent_casual_name?.trim();
  const rosterRole = agent?.display_name_es?.trim() || item.agent;
  const displayName = rosterFirstName || rosterRole;
  const role = rosterFirstName ? rosterRole : null;
  const installationId = agent?.id ?? item.agent_installation_id ?? null;
  // Don't offer a chat link to an agent the merchant hasn't hired (sample findings).
  const canChat = Boolean(installationId && onAgentClick && !item.is_sample);
  const avatarSlug = agent?.avatar_slug ?? item.agent_avatar_slug;
  const accent =
    agent?.accent_color_hex || item.agent_accent_color_hex || DEFAULT_AGENT_ACCENT;

  return (
    <InsightCard
      item={item}
      variant="feed"
      accent={accent}
      handle={{
        name: displayName,
        role,
        avatarSlug,
        isRunning: agent?.is_running,
        onClick:
          canChat && installationId
            ? () => onAgentClick?.(installationId)
            : undefined,
      }}
      recommendationsSlot={
        <InsightActionsDisclosure
          recommendations={item.recommendations ?? []}
          accent={accent}
        />
      }
    />
  );
}

// Maps a recommendation state to a short merchant-facing label + chip tone.
const REC_STATE_LABEL_ES: Record<string, string> = {
  todo: "Por hacer",
  in_progress: "En curso",
  done: "Hecha",
  wont_do: "No la hará",
};

/**
 * InsightActionsDisclosure — a collapsed list of the actions an insight
 * spawned (its recommendations). The Análisis tab is a scan surface, so the
 * actions stay folded behind "N acciones recomendadas ▸" and expand in place
 * to the title + priority + state of each, keeping the insight feed glanceable
 * while making the full action set one click away. Renders nothing when the
 * insight produced no recommendations.
 */
function InsightActionsDisclosure({
  recommendations,
  accent,
}: {
  recommendations: WorkItem["recommendations"];
  accent: string;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const recs = recommendations ?? [];
  if (recs.length === 0) return null;
  return (
    <div className="insightActions" style={{ ["--card-accent" as string]: accent }}>
      <button
        type="button"
        className="insightActions__toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`insightActions__chevron ${open ? "is-open" : ""}`} aria-hidden="true">
          ▸
        </span>
        {recs.length === 1
          ? "1 acción recomendada"
          : `${recs.length} acciones recomendadas`}
      </button>
      {open ? (
        <ul className="insightActions__list">
          {recs.map((rec) => (
            <li key={rec.id}>
              {/* Clickable → the action board (kanban), located on this rec. */}
              <button
                type="button"
                className="insightActions__item insightActions__item--link"
                onClick={() => navigate(`/acciones?rec=${rec.id}`)}
                aria-label={`Ver "${rec.title_es}" en el tablero de acciones`}
              >
                <span className="insightActions__itemTitle">{rec.title_es}</span>
                <span className="insightActions__itemMeta">
                  <Chip size="sm" tone={rec.priority === "alta" ? "danger" : "neutral"}>
                    {`Prioridad ${rec.priority}`}
                  </Chip>
                  {rec.state && rec.state !== "todo" ? (
                    <Chip
                      size="sm"
                      tone={rec.state === "done" ? "success" : "neutral"}
                    >
                      {REC_STATE_LABEL_ES[rec.state] ?? rec.state}
                    </Chip>
                  ) : null}
                  <span className="insightActions__itemArrow" aria-hidden="true">→</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ApprovalCard({
  approval,
  onOpen,
  onAgentClick,
}: {
  approval: ApprovalSummary;
  onOpen: () => void;
  onAgentClick?: () => void;
}) {
  return (
    <article className="approvalCard">
      <div className="itemHeader">
        {onAgentClick ? (
          <button
            type="button"
            className="agentChipLink"
            onClick={onAgentClick}
            aria-label={`Chatear con ${approval.agent.name}`}
          >
            <Chip tone="primary" size="sm">{approval.agent.name}</Chip>
          </button>
        ) : (
          <Chip tone="primary" size="sm">{approval.agent.name}</Chip>
        )}
        <span className="approvalCard__timestamps">
          <time
            className="approvalCard__created"
            dateTime={approval.created_at}
            title={formatDateTime(approval.created_at)}
          >
            Creada {formatRelative(approval.created_at)}
          </time>
          <time
            className="approvalCard__expires"
            dateTime={approval.action.expires_at}
            title={formatDateTime(approval.action.expires_at)}
          >
            vence {formatRelative(approval.action.expires_at)}
          </time>
        </span>
      </div>
      <h3>{actionTitle(approval)}</h3>
      <p>
        {approval.action.expected_impact_es || approval.action.guardrail_es}
      </p>
      <div className="approvalMeta">
        <span>Puede aprobarse</span>
        <span>{approval.action.risk_es || "Riesgo controlado"}</span>
      </div>
      <Button className="secondaryAction" appearance="neutral" onClick={onOpen}>
        Revisar
      </Button>
    </article>
  );
}

// L97 — `plans/17` §8.3. The list view of the Acciones tab: every filtered rec
// as a full interactive card (one-tap state flips + "es duplicada"). The board
// view (the kanban, now this tab's default) is one toggle away via `onSeeAll`.
function PlanTodayPanel({
  rows,
  hasHiredAgents,
  total,
  onSeeAll,
}: {
  rows: PlanTodayRow[];
  hasHiredAgents: boolean;
  total: number;
  onSeeAll?: () => void;
}) {
  const navigate = useNavigate();
  // Pre-hire merchants see no panel — Hoy + the hire CTA already cover that
  // entry point. The panel only appears once the merchant has at least one
  // hired agent (so a real recommendation can theoretically land).
  if (!hasHiredAgents) {
    return null;
  }

  // Header-less rendering — the Acciones tab already explains the surface
  // via its filter bar eyebrow, so the duplicate "Plan de hoy" section title
  // adds noise without information. The list flows directly under the
  // filters; empty state still surfaces a CTA so the merchant has somewhere
  // to go when nothing was recommended.
  return (
    <section className="planTodayPanel planTodayPanel--bare">
      {rows.length > 0 ? (
        <>
          <ul className="planTodayList">
            {rows.map((row) => (
              <PlanTodayInteractiveRow key={row.id} row={row} />
            ))}
          </ul>
          {onSeeAll && total > 0 ? (
            <button
              type="button"
              className="planTodayPanel__seeAll"
              onClick={onSeeAll}
            >
              Ver como tablero →
            </button>
          ) : null}
        </>
      ) : (
        <EmptyState
          title="Tu equipo todavía no tiene recomendaciones para hoy"
          body="Cuando los agentes terminen su próxima corrida, las acciones concretas que valga la pena tomar van a aparecer acá ordenadas por prioridad."
          onAction={() => navigate("/chats")}
          actionLabel="Hablá con un agente"
        />
      )}
    </section>
  );
}

// The Dashboard Acciones recommendation row is the shared `RecommendationCard`
// (variant="feed"); this wrapper owns only the bootstrap optimistic mutation +
// the originating-insight deep link.
function PlanTodayInteractiveRow({ row }: { row: PlanTodayRow }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: PatchRecommendationBody) =>
      patchRecommendation(row.id, payload),
    onMutate: async (payload) => {
      setError(null);
      await queryClient.cancelQueries({ queryKey: ["bootstrap"] });
      const previous = queryClient.getQueryData<BootstrapPayload>(["bootstrap"]);
      if (previous) {
        queryClient.setQueryData<BootstrapPayload>(
          ["bootstrap"],
          mutateBootstrapRecommendationState(previous, row.id, payload),
        );
      }
      return { previous };
    },
    onError: (_err, _payload, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(["bootstrap"], ctx.previous);
      setError("No pudimos guardar el cambio.");
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<BootstrapPayload>(["bootstrap"], (cache) =>
        cache ? replaceBootstrapRecommendation(cache, updated) : cache,
      );
    },
  });

  // The originating-insight "ver completo" link only shows for insights that
  // are in the live feed (so the deep-link lands + highlights, never dead-ends
  // on a superseded observation).
  const liveInsightIds = useMemo(() => {
    const today =
      queryClient.getQueryData<BootstrapPayload>(["bootstrap"])?.today ?? [];
    return new Set(today.map((o) => o.id));
  }, [queryClient]);

  return (
    <SharedRecommendationCard
      rec={row}
      variant="feed"
      onMutate={mutation.mutate}
      isPending={mutation.isPending}
      error={error}
      liveInsightIds={liveInsightIds}
      onOpenInsight={(id) => navigate(`/analisis?insight=${id}`)}
    />
  );
}

function EmptyState({
  title,
  body,
  actionHref,
  actionLabel,
  onAction,
}: {
  title: string;
  body: string;
  actionHref?: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      <p>{body}</p>
      {onAction && actionLabel ? (
        <Button
          className="secondaryAction"
          appearance="neutral"
          onClick={onAction}
        >
          {actionLabel}
        </Button>
      ) : actionHref && actionLabel ? (
        <Button
          as="a"
          className="secondaryAction"
          appearance="neutral"
          href={actionHref}
        >
          {actionLabel}
        </Button>
      ) : null}
    </div>
  );
}

export function FullPageState({
  title,
  body,
  actionLabel,
  onAction,
  actionDisabled = false,
  error = "",
}: {
  title: string;
  body: string;
  actionLabel?: string;
  onAction?: () => void;
  actionDisabled?: boolean;
  error?: string;
}) {
  return (
    <main className="fullPageState">
      <section>
        <p className="eyebrow">
          <TiendaNubeLogo size={16} />
          <span>Agentes Nube</span>
          <AppVersion />
        </p>
        <h1>{title}</h1>
        <p>{body}</p>
        {actionLabel && onAction ? (
          <Button onClick={onAction} disabled={actionDisabled}>
            {actionLabel}
          </Button>
        ) : null}
        {error ? (
          <p className="fullPageState__error" role="alert">
            {error}
          </p>
        ) : null}
      </section>
    </main>
  );
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat(getLocale(), {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

/**
 * Human-friendly relative timestamp ("hace 2 h", "en 3 días"). Uses
 * `Intl.RelativeTimeFormat` so the unit selection is locale-aware. Past
 * timestamps return "hace X"; future timestamps return "en X". `now` is
 * injected so tests can pin a moment.
 */
function formatRelative(value: string, now: Date = new Date()): string {
  const target = new Date(value).getTime();
  if (!Number.isFinite(target)) return "";
  const diffMs = target - now.getTime();
  const absSeconds = Math.abs(diffMs) / 1000;
  const fmt = new Intl.RelativeTimeFormat(getLocale(), { numeric: "auto" });
  const past = diffMs < 0;
  const sign = past ? -1 : 1;
  if (absSeconds < 60) return fmt.format(sign * Math.round(absSeconds), "second");
  if (absSeconds < 3600) return fmt.format(sign * Math.round(absSeconds / 60), "minute");
  if (absSeconds < 86_400) return fmt.format(sign * Math.round(absSeconds / 3600), "hour");
  if (absSeconds < 86_400 * 7)
    return fmt.format(sign * Math.round(absSeconds / 86_400), "day");
  if (absSeconds < 86_400 * 30)
    return fmt.format(sign * Math.round(absSeconds / (86_400 * 7)), "week");
  return fmt.format(sign * Math.round(absSeconds / (86_400 * 30)), "month");
}

function formatUninstallTimestamp(value: string) {
  // Voseo-friendly short form for the reinstall blocker — explicit day-month
  // and HH:mm in the merchant's locale, no year (the event is by definition
  // very recent: the uninstall webhook just arrived).
  return new Intl.DateTimeFormat(getLocale(), {
    day: "2-digit",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function actionTitle(approval: ApprovalSummary) {
  const campaignName = approval.action.target.campaign_name;
  const target =
    typeof campaignName === "string" && campaignName.trim()
      ? campaignName
      : "campaña";
  if (approval.action.action_type === "pause_campaign") {
    return `Pausar ${target}`;
  }
  if (approval.action.action_type === "unpause_campaign") {
    return `Reactivar ${target}`;
  }
  if (approval.action.action_type === "adjust_daily_budget") {
    return `Ajustar presupuesto de ${target}`;
  }
  return approval.action.action_type.replaceAll("_", " ");
}
